import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Plus, Edit, Trash, Search, ChevronLeft, ChevronRight, Filter, Download, ArrowRight, Check,
    Upload, X, Calendar, AlertCircle, List, Package, Eye, Users, ChevronDown, ChevronUp, Clock,
    CheckCircle, ArrowUpDown, ArrowUp, ArrowDown, Settings, Minus, Info
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';
import * as XLSX from 'xlsx';
import PhanBoBaoBiEmTongThe from './PhanBoBaoBiEmTongThe';
import ChuyenBaoBiAnhSangEm from './ChuyenBaoBiAnhSangEm';
import ImportExcelNhapBaoBiVaPhanBo from './ImportExcelNhapBaoBiVaPhanBo';
import ExportExcelNhapBaoBi from './ExportExcelNhapBaoBi';
import HieuChinhTonBBE from './HieuChinhTonBBE';

// Utility function to generate unique ID
const generateUniqueId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';

    // Add timestamp-based prefix (4 chars)
    const timeBase = Date.now().toString(36).toUpperCase().slice(-4);
    result += timeBase.padStart(4, chars.charAt(Math.floor(Math.random() * chars.length)));

    // Add random suffix (4 chars)
    for (let i = 0; i < 4; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
};

// Date formatting utilities
const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Parse date for filtering
const parseVNDate = (dateString) => {
    if (!dateString) return null;
    if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/').map(Number);
        const date = new Date(year, month - 1, day);
        date.setHours(0, 0, 0, 0);
        return date;
    }
    const date = new Date(dateString);
    date.setHours(0, 0, 0, 0);
    return date;
};

// Format number - Hiển thị số nguyên với dấu phân cách hàng nghìn
const formatNumber = (number) => {
    if (number === null || number === undefined || number === '') return '0';
    const num = parseInt(number, 10);
    if (isNaN(num)) return '0';
    return num.toLocaleString('vi-VN');
};

// Parse number safely - hỗ trợ số thập phân
const parseNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.max(0, num);
};

// Format currency
const formatCurrency = (amount) => {
    const num = parseNumber(amount);
    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND'
    }).format(num);
};

// Calculate thực nhận = nhập bao bì - % trừ - trả BB - TRẢ VỀ NUMBER NGUYÊN
const calculateThucNhan = (nhapBaoBi, percent = 3, traBB = 0) => {
    const nhap = parseNumber(nhapBaoBi);
    const truBaoBi = Math.round(nhap * (percent / 100));
    const tra = parseNumber(traBB);
    const thucNhan = nhap - truBaoBi - tra;
    return {
        truBaoBi: truBaoBi,
        thucNhan: thucNhan
    };
};

// Clean text value - Remove dấu nháy đơn (') ở đầu nếu có
// Dùng để hiển thị "SỐ XE" và "SỐ LÔ" từ database
const cleanTextValue = (value) => {
    if (!value) return '';
    const strValue = String(value).trim();
    // Remove leading single quote nếu có
    return strValue.startsWith("'") ? strValue.substring(1) : strValue;
};

// Improved pagination component
const Pagination = ({ currentPage, totalPages, onPageChange, itemsPerPage, onItemsPerPageChange, totalItems }) => (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
        {/* Phần chọn số dòng hiển thị */}
        <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 whitespace-nowrap">Hiển thị:</label>
            <select
                value={itemsPerPage}
                onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white"
            >
                <option value={10}>10 dòng</option>
                <option value={25}>25 dòng</option>
                <option value={50}>50 dòng</option>
                <option value={100}>100 dòng</option>
            </select>
            <span className="text-sm text-gray-600">
                (Tổng: {totalItems} bản ghi)
            </span>
        </div>

        {/* Phần nút phân trang */}
        <div className="flex justify-center items-center space-x-2">
            <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-md ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:bg-indigo-50'}`}
            >
                <ChevronLeft className="h-5 w-5" />
            </button>

            <div className="flex space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                        pageNum = i + 1;
                    } else if (currentPage <= 3) {
                        pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                    } else {
                        pageNum = currentPage - 2 + i;
                    }
                    return (
                        <button
                            key={pageNum}
                            onClick={() => onPageChange(pageNum)}
                            className={`w-8 h-8 flex items-center justify-center rounded-md ${currentPage === pageNum
                                ? 'bg-indigo-600 text-white'
                                : 'text-gray-700 hover:bg-indigo-50'
                                }`}
                        >
                            {pageNum}
                        </button>
                    );
                })}
            </div>

            <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-md ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:bg-indigo-50'}`}
            >
                <ChevronRight className="h-5 w-5" />
            </button>

            <span className="text-sm text-gray-600 ml-2">
                Trang {currentPage} / {totalPages || 1}
            </span>
        </div>
    </div>
);

// Component Autocomplete cho Số xe, NCC, Số lô
const AutocompleteInput = ({ label, value, onChange, options, placeholder, required = false }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (value) {
            const filtered = options.filter(option =>
                option.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredOptions(filtered);
        } else {
            setFilteredOptions(options);
        }
    }, [value, options]);

    useEffect(() => {
        if (!showDropdown) return;

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDropdown]);

    return (
        <div className="relative" ref={dropdownRef}>
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {label} {required && <span className="text-red-500">*</span>}
                </label>
            )}
            <input
                type="text"
                placeholder={placeholder}
                className="p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                    setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
                required={required}
            />
            {showDropdown && filteredOptions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredOptions.map((option, index) => (
                        <div
                            key={index}
                            className="px-4 py-2 hover:bg-indigo-50 cursor-pointer text-sm"
                            onClick={() => {
                                onChange(option);
                                setShowDropdown(false);
                            }}
                        >
                            {option}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const NhapBaoBiManagement = () => {
    const navigate = useNavigate();

    // State Management - core data
    const [packagingData, setPackagingData] = useState([]);
    const [detailReports, setDetailReports] = useState([]);
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grouped'

    // State cho autocomplete
    const [soXeOptions, setSoXeOptions] = useState([]);
    const [nccOptions, setNccOptions] = useState([]);
    const [soLoOptions, setSoLoOptions] = useState([]);

    // State - UI controls
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [expandedDetailGroups, setExpandedDetailGroups] = useState(new Set());
    const [expandedGroupDetailGroups, setExpandedGroupDetailGroups] = useState(new Set());

    const [showPhanBoEmTongTheModal, setShowPhanBoEmTongTheModal] = useState(false);
    const [showImportFullModal, setShowImportFullModal] = useState(false);
    const [showExportExcelModal, setShowExportExcelModal] = useState(false);

    // sorting
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'asc'
    });

    const [filters, setFilters] = useState({
        searchFilter: '',
        startDate: null,
        endDate: null
    });

    // chuyển bao bì
    const [showChuyenBaoBiAnhSangEmModal, setShowChuyenBaoBiAnhSangEmModal] = useState(false);
    const [chuyenBaoBiData, setChuyenBaoBiData] = useState([]);

    // hiệu chỉnh tồn BBE
    const [showHieuChinhTonBBEModal, setShowHieuChinhTonBBEModal] = useState(false);
    const [hieuChinhTonBBEData, setHieuChinhTonBBEData] = useState([]);

    // detail modal
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);

    // modal hiển thị thông tin nhận BBA/BBE
    const [showThucNhanModal, setShowThucNhanModal] = useState(false);
    const [thucNhanType, setThucNhanType] = useState(null); // 'BBA' hoặc 'BBE'

    // confirm modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmMessage, setConfirmMessage] = useState("");
    const [confirmTitle, setConfirmTitle] = useState("");
    const [isConfirmLoading, setIsConfirmLoading] = useState(false);

    // danh sách TỔ
    const [teamList, setTeamList] = useState([]);
    // QUANLYLO
    const [quanlyLoData, setQuanlyLoData] = useState([]);

    // ====== STATE MỚI: NHẬP NHIỀU XE ======
    const [currentDate, setCurrentDate] = useState(formatDateForInput(new Date()));
    const [vehicleRows, setVehicleRows] = useState([]);

    // tạo dòng xe trống
    const createEmptyVehicleRow = () => ({
        tempId: generateUniqueId(),
        ID: '',
        soXe: '',
        ncc: '',
        soLo: '',
        ghiChu: '',
        nhapBB: '',
        percentTru: 3,
        truBB: 0,
        traBB: '', // TRẢ BB (KG) - người dùng nhập
        thucNhanBB: 0,
        thucNhanBBE: '', // THỰC NHẬN BBE (KG) - người dùng nhập
        thucNhanBBA: 0, // THỰC NHẬN BBA (KG) - tự động tính = thucNhanBB - thucNhanBBE
        soDu: '', // SỐ DƯ (cột ảo - không lưu DB) - người dùng nhập
        soDay: '', // SỐ DÂY (cột thật - lưu DB) - người dùng nhập
        khoiLuongMoiDay: 0, // Khối lượng mỗi dây = (thucNhanBBA - soDu) / soDay
        allocations: [] // {id, teamName, soDay, khoiLuong}
    });

    // Helper
    const handleCloseConfirmModal = () => {
        setShowConfirmModal(false);
        setConfirmAction(null);
        setConfirmMessage("");
        setConfirmTitle("");
        setIsConfirmLoading(false);
    };

    // Data fetching
    useEffect(() => {
        fetchPackagingData();
        fetchPhanBoData();
        fetchAutoCompleteOptions();
        fetchChuyenBaoBiData();
        fetchHieuChinhTonBBEData();
    }, []);

    const fetchPackagingData = async () => {
        try {
            const response = await authUtils.apiRequest('NHAPBAOBI', 'Find', {});
            setPackagingData(response);
        } catch (error) {
            console.error('Error fetching packaging data:', error);
            toast.error('Lỗi khi tải danh sách nhập bao bì');
        }
    };

    const fetchPhanBoData = async () => {
        try {
            const response = await authUtils.apiRequest('PHANBOBAOBI', 'Find', {});
            setDetailReports(response);
        } catch (error) {
            console.error('Error fetching phanbo data:', error);
            toast.error('Lỗi khi tải dữ liệu phân bổ');
        }
    };

    const fetchChuyenBaoBiData = async () => {
        try {
            const response = await authUtils.apiRequest('CHUYENBAOBI', 'Find', {});
            setChuyenBaoBiData(response);
        } catch (error) {
            console.error('Error fetching chuyen bao bi data:', error);
            toast.error('Lỗi khi tải dữ liệu chuyển bao bì');
        }
    };

    const fetchHieuChinhTonBBEData = async () => {
        try {
            const response = await authUtils.apiRequest('HIEU_CHINH_TON_BBE', 'Find', {});
            setHieuChinhTonBBEData(response);
        } catch (error) {
            console.error('Error fetching hieu chinh ton BBE data:', error);
            // Không toast error vì bảng có thể chưa có data
        }
    };

    // Fetch dữ liệu cho autocomplete
    const fetchAutoCompleteOptions = async () => {
        try {
            const nhapBaoBiData = await authUtils.apiRequest('NHAPBAOBI', 'Find', {});
            const uniqueSoXe = [...new Set(nhapBaoBiData.map(item => cleanTextValue(item['SỐ XE'])).filter(Boolean))];
            setSoXeOptions(uniqueSoXe);

            const dskhData = await authUtils.apiRequestKHO('DSKH', 'Find', {});
            const nccList = dskhData
                .filter(item => item['PHAN_LOAI'] === 'NCC')
                .map(item => item['TEN_KHACHHANG'])
                .filter(Boolean);
            setNccOptions(nccList);

            const qlLoData = await authUtils.apiRequest('QUANLYLO', 'Find', {});
            setQuanlyLoData(qlLoData);

            const allSoLo = qlLoData.map(item => item['Số lô']).filter(Boolean);
            setSoLoOptions(allSoLo);
        } catch (error) {
            console.error('Error fetching autocomplete options:', error);
            toast.error('Lỗi khi tải dữ liệu gợi ý');
        }
    };

    // Fetch danh sách TỔ
    const fetchTeamList = async () => {
        try {
            const teamResponse = await authUtils.apiRequest('TO_PBNS', 'Find', {});

            // Lấy danh sách tổ duy nhất (chỉ lấy tổ có Bao bì anh)
            const uniqueTeams = [...new Set(
                teamResponse
                    .filter(t => t['NHÓM'] === 'Bao bì anh')
                    .map(t => t['TỔ'])
                    .filter(Boolean) // Loại bỏ giá trị null/undefined
            )];

            setTeamList(uniqueTeams);
        } catch (error) {
            console.error('Error fetching team list:', error);
            toast.error('Lỗi khi tải danh sách tổ');
        }
    };

    // cập nhật options Số lô khi chọn NCC
    useEffect(() => {
        if (quanlyLoData.length === 0) return;

        // Khởi tạo tất cả Số lô khi load lần đầu
        const allSoLo = quanlyLoData.map(item => item['Số lô']).filter(Boolean);
        setSoLoOptions(allSoLo);
    }, [quanlyLoData]);

    // Hàm lọc Số lô theo NCC đã chọn
    const getSoLoOptionsByNCC = useCallback((nccSelected) => {
        if (!nccSelected || !quanlyLoData.length) {
            return soLoOptions; // Trả về tất cả nếu chưa chọn NCC
        }

        const filteredSoLo = quanlyLoData
            .filter(item => item['Nhà cung cấp'] === nccSelected)
            .map(item => item['Số lô'])
            .filter(Boolean);

        return filteredSoLo;
    }, [quanlyLoData, soLoOptions]);

    // Hàm tính tổng THỰC NHẬN BB (KG) đã nhập cho Số lô
    const getTotalThucNhanBBByLo = useCallback((soLo) => {
        if (!soLo || !packagingData.length) return 0;

        const total = packagingData
            .filter(item => item['SỐ LÔ'] === soLo)
            .reduce((sum, item) => sum + parseNumber(item['THỰC NHẬN BB (KG)']), 0);

        return total;
    }, [packagingData]);

    // Hàm lấy thông tin Số lô từ QUANLYLO
    const getLoInfo = useCallback((soLo) => {
        if (!soLo || !quanlyLoData.length) return null;

        return quanlyLoData.find(item => item['Số lô'] === soLo);
    }, [quanlyLoData]);

    // ====== PHÂN BỔ & TRẠNG THÁI – dùng cho list view ======
    // Index phân bổ theo ID_NHAPBAOBI (đã sắp theo ngày giảm dần) — chỉ tính lại khi detailReports đổi,
    // thay vì quét toàn bộ detailReports cho từng phiếu ở mỗi lần render.
    const reportsByNhapBaoBi = useMemo(() => {
        const map = new Map();
        for (const report of detailReports) {
            const key = report.ID_NHAPBAOBI;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(report);
        }
        for (const list of map.values()) {
            list.sort((a, b) => new Date(b['NGÀY']) - new Date(a['NGÀY']));
        }
        return map;
    }, [detailReports]);

    const getDistributionStatus = (record) => {
        const relatedReports = reportsByNhapBaoBi.get(record.ID) || [];

        const totalAllocated = relatedReports.reduce((sum, report) => sum + parseNumber(report['KHỐI LƯỢNG']), 0);
        const availableBBA = parseNumber(record['THỰC NHẬN BBA (KG)']);

        return {
            totalReports: relatedReports.length,
            totalAllocated: totalAllocated,
            isFullyAllocated: totalAllocated >= availableBBA && availableBBA > 0,
            hasAllocation: relatedReports.length > 0,
            relatedReports: relatedReports
        };
    };

    const AllocationSummary = React.memo(({ record }) => {
        const status = useMemo(() => getDistributionStatus(record), [record]);

        // Không có phân bổ -> để trống
        if (!status.hasAllocation) {
            return null; // hoặc <span className="text-xs text-gray-400 italic">Chưa có phân bổ</span>
        }

        // Nhóm theo TỔ
        const groupedByTo = status.relatedReports.reduce((acc, report) => {
            const to = report['TỔ'] || 'Khác';
            if (!acc[to]) acc[to] = 0;
            acc[to] += parseNumber(report['KHỐI LƯỢNG']);
            return acc;
        }, {});

        const toSummary = Object.entries(groupedByTo)
            .map(([to, total]) => `${to}: ${formatNumber(total)} KG`)
            .join(', ');

        return (
            <div className="text-xs text-indigo-700 font-medium leading-snug">
                {toSummary}
            </div>
        );
    });


    const DetailReportsSection = ({ record }) => {
        const status = getDistributionStatus(record);
        if (!status.hasAllocation) return null;

        const groupedByTo = status.relatedReports.reduce((acc, report) => {
            const to = report['TỔ'] || 'Chưa phân tổ';
            if (!acc[to]) {
                acc[to] = [];
            }
            acc[to].push(report);
            return acc;
        }, {});

        return (
            <tr>
                <td colSpan="13" className="px-4 py-0">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-4 my-2 rounded-r-lg">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-lg font-semibold text-blue-800 flex items-center gap-2">
                                <Package className="w-5 h-5" />
                                Chi tiết phân bổ BBA ({status.totalReports} bản ghi - {Object.keys(groupedByTo).length} tổ)
                            </h4>
                            <div className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-lg">
                                Tổng đã phân bổ BBA: {formatNumber(status.totalAllocated)} KG
                            </div>
                        </div>

                        <div className="space-y-4">
                            {Object.entries(groupedByTo).map(([to, reports]) => {
                                const totalAmount = reports.reduce((sum, r) => sum + parseNumber(r['KHỐI LƯỢNG']), 0);

                                return (
                                    <div key={to} className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                                        <div className="bg-gradient-to-r from-indigo-100 to-blue-100 px-3 py-2.5 border-b border-blue-200">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <span className="px-3 py-1 bg-indigo-600 text-white rounded-lg text-sm font-bold">
                                                        {to}
                                                    </span>
                                                    <span className="text-sm text-gray-700">
                                                        {reports.length} bản ghi
                                                    </span>
                                                </div>
                                                <div className="text-sm font-bold text-green-700">
                                                    Tổng: {formatNumber(totalAmount)} KG
                                                </div>
                                            </div>
                                        </div>

                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-blue-200">
                                                <thead className="bg-blue-50">
                                                    <tr>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-blue-700 uppercase">Ngày</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-blue-700 uppercase">Tên hàng</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-blue-700 uppercase">Số dây</th>
                                                        <th className="px-3 py-2 text-left text-xs font-medium text-blue-700 uppercase">Khối lượng</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-blue-100">
                                                    {reports.map((report, index) => (
                                                        <tr key={report.IDPB} className={`${index % 2 === 0 ? 'bg-white' : 'bg-blue-25'} hover:bg-blue-50 transition-colors`}>
                                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                                                                {new Date(report['NGÀY']).toLocaleDateString('vi-VN')}
                                                            </td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {report['TÊN HÀNG']}
                                                            </td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-blue-600">
                                                                {report['SỐ DÂY'] || '-'}
                                                            </td>
                                                            <td className="px-3 py-2 whitespace-nowrap text-sm font-bold text-green-700">
                                                                {formatNumber(report['KHỐI LƯỢNG'])} KG
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </td>
            </tr>
        );
    };

    const StatisticCards = ({ data }) => {
        const stats = useMemo(() => {
            const totalNhapBB = data.reduce((sum, record) => sum + parseNumber(record['NHẬP BB (KG)']) - parseNumber(record['TRẢ BB (KG)']), 0);
            const totalTruBB = data.reduce((sum, record) => sum + parseNumber(record['TRỪ BB (KG)']), 0);
            const totalTraBB = data.reduce((sum, record) => sum + parseNumber(record['TRẢ BB (KG)']), 0);
            const totalThucNhanBB = data.reduce((sum, record) => sum + parseNumber(record['THỰC NHẬN BB (KG)']), 0);
            const totalThucNhanBBA = data.reduce((sum, record) => sum + parseNumber(record['THỰC NHẬN BBA (KG)']), 0);
            const totalThucNhanBBE = data.reduce((sum, record) => sum + parseNumber(record['THỰC NHẬN BBE (KG)']), 0);

            let totalAllocatedBBA = 0;
            data.forEach(record => {
                const status = getDistributionStatus(record);
                totalAllocatedBBA += status.totalAllocated;
            });

            // Hàm helper lọc theo ngày
            const filterByDateRange = (item, dateField) => {
                if (!filters.startDate && !filters.endDate) return true;

                const itemDateStr = formatDateForInput(item[dateField]);
                if (!itemDateStr) return false;

                const itemDate = new Date(itemDateStr);
                itemDate.setHours(0, 0, 0, 0);

                if (filters.startDate && filters.endDate) {
                    const startDate = new Date(filters.startDate);
                    startDate.setHours(0, 0, 0, 0);
                    const endDate = new Date(filters.endDate);
                    endDate.setHours(23, 59, 59, 999);
                    return itemDate >= startDate && itemDate <= endDate;
                } else if (filters.startDate) {
                    const startDate = new Date(filters.startDate);
                    startDate.setHours(0, 0, 0, 0);
                    return itemDate >= startDate;
                } else if (filters.endDate) {
                    const endDate = new Date(filters.endDate);
                    endDate.setHours(23, 59, 59, 999);
                    return itemDate <= endDate;
                }
                return true;
            };

            // Tính tổng phân bổ BBE từ detailReports (PHANBOBAOBI) - LỌC THEO NGÀY
            const totalAllocatedBBE = detailReports
                .filter(pb => pb['TÊN HÀNG'] === 'Bao bì em' && !pb.ID_NHAPBAOBI)
                .filter(pb => filterByDateRange(pb, 'NGÀY'))
                .reduce((sum, pb) => sum + parseNumber(pb['KHỐI LƯỢNG']), 0);

            // Tính tồn BBE = THỰC NHẬN BBE - Đã phân bổ BBE
            const tonBBE = totalThucNhanBBE - totalAllocatedBBE;

            // Tính tổng chuyển BBA sang BBE từ chuyenBaoBiData (CHUYENBAOBI) - LỌC THEO NGÀY
            const totalChuyenBBA_BBE = chuyenBaoBiData
                .filter(cbb => filterByDateRange(cbb, 'NGÀY'))
                .reduce((sum, cbb) => sum + parseNumber(cbb['KHỐI LƯỢNG']), 0);

            // Tính tổng hiệu chỉnh tồn BBE - LỌC THEO NGÀY
            const totalHieuChinhBBE = hieuChinhTonBBEData
                .filter(hc => filterByDateRange(hc, 'NGÀY HIỆU CHỈNH'))
                .reduce((sum, hc) => sum + parseNumber(hc['SỐ LƯỢNG HIỆU CHỈNH']), 0);

            // Tồn BBE sau hiệu chỉnh = Tồn BBE + Hiệu chỉnh
            const tonBBESauHieuChinh = tonBBE + totalHieuChinhBBE;

            return {
                totalRecords: data.length,
                totalNhapBB,
                totalTruBB,
                totalTraBB,
                totalThucNhanBB,
                totalThucNhanBBA,
                totalThucNhanBBE,
                totalAllocatedBBA,
                totalAllocatedBBE,
                tonBBE: Math.max(0, tonBBE),
                totalChuyenBBA_BBE,
                totalHieuChinhBBE,
                tonBBESauHieuChinh
            };
        }, [data, detailReports, chuyenBaoBiData, hieuChinhTonBBEData, filters.startDate, filters.endDate]);

        const cardData = [
            {
                title: "Tổng số xe",
                value: stats.totalRecords,
                icon: <Package className="w-5 h-5" />,
                bgColor: "bg-gradient-to-r from-purple-500 to-purple-600",
                textColor: "text-white",
                onClick: null
            },
            {
                title: "Tổng nhập BB",
                value: `${formatNumber(stats.totalNhapBB)} KG`,
                icon: <Package className="w-5 h-5" />,
                bgColor: "bg-gradient-to-r from-blue-500 to-blue-600",
                textColor: "text-white",
                onClick: null
            },
            {
                title: "Trừ BB",
                value: `${formatNumber(stats.totalTruBB)} KG`,
                icon: <Minus className="w-5 h-5" />,
                bgColor: "bg-gradient-to-r from-red-500 to-red-600",
                textColor: "text-white",
                onClick: null
            },
            {
                title: "Trả BB",
                value: `${formatNumber(stats.totalTraBB)} KG`,
                icon: <X className="w-5 h-5" />,
                bgColor: "bg-gradient-to-r from-yellow-500 to-yellow-600",
                textColor: "text-white",
                onClick: () => {
                    setThucNhanType('TRABB');
                    setShowThucNhanModal(true);
                }
            },
            {
                title: "Thực nhận BB",
                value: `${formatNumber(stats.totalThucNhanBB)} KG`,
                icon: <Check className="w-5 h-5" />,
                bgColor: "bg-gradient-to-r from-green-500 to-green-600",
                textColor: "text-white",
                onClick: null
            },
            {
                title: "Thực nhận BBA",
                value: `${formatNumber(stats.totalThucNhanBBA)} KG`,
                icon: <Package className="w-5 h-5" />,
                bgColor: "bg-gradient-to-r from-teal-500 to-teal-600",
                textColor: "text-white",
                onClick: () => {
                    setThucNhanType('BBA');
                    setShowThucNhanModal(true);
                }
            },
            {
                title: "Thực nhận BBE",
                value: `${formatNumber(stats.totalThucNhanBBE)} KG`,
                icon: <Package className="w-5 h-5" />,
                bgColor: "bg-gradient-to-r from-pink-500 to-pink-600",
                textColor: "text-white",
                onClick: () => {
                    setThucNhanType('BBE');
                    setShowThucNhanModal(true);
                }
            },
            {
                title: "Phân bổ BBE",
                value: `${formatNumber(stats.totalAllocatedBBE)} KG`,
                icon: <Users className="w-5 h-5" />,
                bgColor: "bg-gradient-to-r from-fuchsia-500 to-fuchsia-600",
                textColor: "text-white",
                onClick: () => navigate('/chitiet_phanbo', { state: { tab: 'em' } })
            },
            {
                title: "Hiệu chỉnh BBE",
                value: `${stats.totalHieuChinhBBE >= 0 ? '+' : ''}${formatNumber(stats.totalHieuChinhBBE)} KG`,
                icon: <Settings className="w-5 h-5" />,
                bgColor: "bg-gradient-to-r from-cyan-500 to-cyan-600",
                textColor: "text-white",
                onClick: () => setShowHieuChinhTonBBEModal(true)
            },
            {
                title: "Tồn BBE",
                value: `${formatNumber(stats.tonBBESauHieuChinh)} KG`,
                icon: <AlertCircle className="w-5 h-5" />,
                bgColor: "bg-gradient-to-r from-rose-500 to-rose-600",
                textColor: "text-white",
                onClick: () => navigate('/chitiet_phanbo', { state: { tab: 'em' } })
            },
            {
                title: "Chuyển BBA-BBE",
                value: `${formatNumber(stats.totalChuyenBBA_BBE)} KG`,
                icon: <ArrowRight className="w-5 h-5" />,
                bgColor: "bg-gradient-to-r from-orange-500 to-orange-600",
                textColor: "text-white",
                onClick: () => navigate('/chitiet_phanbo', { state: { tab: 'chuyen' } })
            }
        ];

        return (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-11 gap-2 mb-6">
                {cardData.map((card, index) => (
                    <div
                        key={index}
                        className={`${card.bgColor} rounded-xl shadow-lg p-2 text-center transform hover:scale-105 transition-all duration-200 ${card.onClick ? 'cursor-pointer' : ''}`}
                        onClick={card.onClick}
                    >
                        <div className="flex flex-col items-center">
                            <div className={`${card.textColor} opacity-80 mb-1`}>
                                {card.icon}
                            </div>
                            <h3 className={`${card.textColor} text-xs font-medium mb-1 text-center leading-tight`}>
                                {card.title}
                            </h3>
                            <p className={`${card.textColor} text-sm lg:text-base font-bold`}>
                                {card.value}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const handleFilterDateChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value || null
        }));
    };

    // toggle detail rows in list view
    const toggleDetailExpansion = (recordId) => {
        const newExpanded = new Set(expandedDetailGroups);
        if (newExpanded.has(recordId)) newExpanded.delete(recordId);
        else newExpanded.add(recordId);
        setExpandedDetailGroups(newExpanded);
    };

    const toggleGroupDetailExpansion = (recordId) => {
        const newExpanded = new Set(expandedGroupDetailGroups);
        if (newExpanded.has(recordId)) newExpanded.delete(recordId);
        else newExpanded.add(recordId);
        setExpandedGroupDetailGroups(newExpanded);
    };

    // sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) {
            return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
        }
        return sortConfig.direction === 'asc'
            ? <ArrowUp className="w-4 h-4 text-indigo-600" />
            : <ArrowDown className="w-4 h-4 text-indigo-600" />;
    };

    // Detail modal functions
    const handleViewDetail = (record) => {
        setSelectedRecord(record);
        setShowDetailModal(true);
    };

    const handleCloseDetail = () => {
        setShowDetailModal(false);
        setSelectedRecord(null);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };

    // ====== MỞ / ĐÓNG MODAL NHIỀU XE ======
    const handleOpen = useCallback(async (record = null) => {
        // ✅ MỞ MODAL NGAY LẬP TỨC
        setOpen(true);

        if (record) {
            const dateStr = formatDateForInput(record['NGÀY THÁNG']);
            setCurrentDate(dateStr);

            // ✅ CHỈ LẤY RECORD ĐƯỢC CHỌN, KHÔNG LẤY TẤT CẢ XE CÙNG NGÀY
            const relatedReports = detailReports.filter(d => d.ID_NHAPBAOBI === record.ID);
            const allocations = relatedReports.map(d => {
                const soDayStr = String(d['SỐ DÂY'] || '');
                // Parse soDay và isDongChuaSoDu từ format "X + Y KG"
                const isDongChuaSoDu = soDayStr.includes('+');
                const soDay = isDongChuaSoDu ? soDayStr.split('+')[0].trim() : soDayStr;

                return {
                    id: d.IDPB,
                    teamName: d['TỔ'],
                    soDay,
                    khoiLuong: parseNumber(d['KHỐI LƯỢNG']),
                    isDongChuaSoDu
                };
            });

            const thucNhanBB = parseNumber(record['THỰC NHẬN BB (KG)']);
            const thucNhanBBE = parseNumber(record['THỰC NHẬN BBE (KG)']);
            const thucNhanBBA = parseNumber(record['THỰC NHẬN BBA (KG)']);
            const soDay = parseNumber(record['SỐ DÂY']);

            // Tính khối lượng mỗi dây (giả sử soDu = 0 khi load)
            const soDu = 0;
            const khoiLuongMoiDay = soDay > 0 ? (thucNhanBBA - soDu) / soDay : 0;

            const editRow = {
                tempId: generateUniqueId(),
                ID: record.ID,
                soXe: cleanTextValue(record['SỐ XE']),
                ncc: String(record['NCC'] || ''),
                soLo: cleanTextValue(record['SỐ LÔ']),
                ghiChu: String(record['GHI CHÚ'] || ''),
                nhapBB: parseNumber(record['NHẬP BB (KG)']),
                percentTru: 3,
                truBB: parseNumber(record['TRỪ BB (KG)']),
                traBB: parseNumber(record['TRẢ BB (KG)']),
                thucNhanBB,
                thucNhanBBE,
                thucNhanBBA,
                soDu, // Cột ảo, không lưu DB
                soDay,
                khoiLuongMoiDay,
                allocations
            };

            // ✅ CHỈ SET 1 ROW DUY NHẤT
            setVehicleRows([editRow]);

            // ✅ FETCH TEAM LIST SONG SONG (không chờ)
            fetchTeamList();
        } else {
            // Tạo mới - giữ nguyên logic cũ
            const todayStr = formatDateForInput(new Date());
            setCurrentDate(todayStr);
            setVehicleRows([createEmptyVehicleRow()]);

            // ✅ FETCH TEAM LIST SONG SONG
            fetchTeamList();
        }
    }, [packagingData, detailReports]);


    const handleClose = useCallback(() => {
        setOpen(false);
        setVehicleRows([]);
    }, []);

    // ====== UPDATE FIELD CHO TỪNG XE ======
    const updateVehicleField = (tempId, field, value) => {
        setVehicleRows(prev =>
            prev.map(row => {
                if (row.tempId !== tempId) return row;
                let updated = { ...row };

                if (field === 'soXe' || field === 'ncc' || field === 'soLo' || field === 'ghiChu') {
                    updated[field] = String(value);
                } else if (field === 'percentTru') {
                    const percent = Math.max(0, Math.min(100, Number(value) || 0));
                    updated.percentTru = percent;
                    if (updated.nhapBB > 0) {
                        const { truBaoBi, thucNhan } = calculateThucNhan(updated.nhapBB, percent, updated.traBB);
                        updated.truBB = truBaoBi;
                        updated.thucNhanBB = thucNhan;
                        // Tính lại THỰC NHẬN BBA
                        const thucNhanBBE = parseNumber(updated.thucNhanBBE);
                        updated.thucNhanBBA = Math.max(0, thucNhan - thucNhanBBE);
                    }
                } else if (field === 'nhapBB') {
                    const nhap = parseNumber(value);
                    updated.nhapBB = nhap;
                    const { truBaoBi, thucNhan } = calculateThucNhan(
                        nhap,
                        updated.percentTru ?? 3,
                        updated.traBB
                    );
                    updated.truBB = truBaoBi;
                    updated.thucNhanBB = thucNhan;
                    // Tính lại THỰC NHẬN BBA
                    const thucNhanBBE = parseNumber(updated.thucNhanBBE);
                    updated.thucNhanBBA = Math.max(0, thucNhan - thucNhanBBE);

                    // Recalculate khối lượng mỗi dây
                    const soDay = parseNumber(updated.soDay);
                    const soDu = parseNumber(updated.soDu);
                    if (soDay > 0) {
                        updated.khoiLuongMoiDay = Math.max(0, (updated.thucNhanBBA - soDu) / soDay);
                    } else {
                        updated.khoiLuongMoiDay = 0;
                    }

                    // Recalculate tất cả allocations - chỉ khi có khối lượng mỗi dây
                    updated.allocations = updated.allocations.map((a) => {
                        const soDayAlloc = parseNumber(a.soDay);
                        let khoiLuong = a.khoiLuong;
                        if (updated.khoiLuongMoiDay > 0) {
                            khoiLuong = soDayAlloc * updated.khoiLuongMoiDay;
                            if (a.isDongChuaSoDu && soDu > 0) {
                                khoiLuong += soDu;
                            }
                        }
                        return { ...a, khoiLuong };
                    });
                } else if (field === 'truBB') {
                    const tru = parseNumber(value);
                    updated.truBB = tru;
                    const nhap = parseNumber(updated.nhapBB);
                    const tra = parseNumber(updated.traBB);
                    updated.thucNhanBB = Math.max(0, nhap - tru - tra);
                    // Tính lại THỰC NHẬN BBA
                    const thucNhanBBE = parseNumber(updated.thucNhanBBE);
                    updated.thucNhanBBA = Math.max(0, updated.thucNhanBB - thucNhanBBE);

                    // Recalculate khối lượng mỗi dây
                    const soDay = parseNumber(updated.soDay);
                    const soDu = parseNumber(updated.soDu);
                    if (soDay > 0) {
                        updated.khoiLuongMoiDay = Math.max(0, (updated.thucNhanBBA - soDu) / soDay);
                    } else {
                        updated.khoiLuongMoiDay = 0;
                    }

                    // Recalculate tất cả allocations - chỉ khi có khối lượng mỗi dây
                    updated.allocations = updated.allocations.map((a) => {
                        const soDayAlloc = parseNumber(a.soDay);
                        let khoiLuong = a.khoiLuong;
                        if (updated.khoiLuongMoiDay > 0) {
                            khoiLuong = soDayAlloc * updated.khoiLuongMoiDay;
                            if (a.isDongChuaSoDu && soDu > 0) {
                                khoiLuong += soDu;
                            }
                        }
                        return { ...a, khoiLuong };
                    });
                } else if (field === 'traBB') {
                    // TRẢ BB (KG) - người dùng nhập
                    const tra = parseNumber(value);
                    updated.traBB = tra;
                    const nhap = parseNumber(updated.nhapBB);
                    const tru = parseNumber(updated.truBB);
                    updated.thucNhanBB = Math.max(0, nhap - tru - tra);
                    // Tính lại THỰC NHẬN BBA
                    const thucNhanBBE = parseNumber(updated.thucNhanBBE);
                    updated.thucNhanBBA = Math.max(0, updated.thucNhanBB - thucNhanBBE);

                    // Recalculate khối lượng mỗi dây
                    const soDay = parseNumber(updated.soDay);
                    const soDu = parseNumber(updated.soDu);
                    if (soDay > 0) {
                        updated.khoiLuongMoiDay = Math.max(0, (updated.thucNhanBBA - soDu) / soDay);
                    } else {
                        updated.khoiLuongMoiDay = 0;
                    }

                    // Recalculate tất cả allocations - chỉ khi có khối lượng mỗi dây
                    updated.allocations = updated.allocations.map((a) => {
                        const soDayAlloc = parseNumber(a.soDay);
                        let khoiLuong = a.khoiLuong;
                        if (updated.khoiLuongMoiDay > 0) {
                            khoiLuong = soDayAlloc * updated.khoiLuongMoiDay;
                            // Chỉ dòng được chọn chứa số dư mới cộng thêm
                            if (a.isDongChuaSoDu && soDu > 0) {
                                khoiLuong += soDu;
                            }
                        }
                        return { ...a, khoiLuong };
                    });
                } else if (field === 'thucNhanBB') {
                    const thuc = parseNumber(value);
                    updated.thucNhanBB = thuc;
                    // Tính lại THỰC NHẬN BBA
                    const thucNhanBBE = parseNumber(updated.thucNhanBBE);
                    updated.thucNhanBBA = Math.max(0, thuc - thucNhanBBE);
                } else if (field === 'thucNhanBBE') {
                    // THỰC NHẬN BBE (KG) - người dùng nhập
                    const bbe = parseNumber(value);
                    updated.thucNhanBBE = bbe;
                    // Tính THỰC NHẬN BBA = THỰC NHẬN BB - THỰC NHẬN BBE
                    const thucNhanBB = parseNumber(updated.thucNhanBB);
                    updated.thucNhanBBA = Math.max(0, thucNhanBB - bbe);

                    // Recalculate khối lượng mỗi dây
                    const soDay = parseNumber(updated.soDay);
                    const soDu = parseNumber(updated.soDu);
                    if (soDay > 0) {
                        updated.khoiLuongMoiDay = Math.max(0, (updated.thucNhanBBA - soDu) / soDay);
                    } else {
                        updated.khoiLuongMoiDay = 0;
                    }

                    // Recalculate tất cả allocations - chỉ khi có khối lượng mỗi dây
                    updated.allocations = updated.allocations.map((a) => {
                        const soDayAlloc = parseNumber(a.soDay);
                        let khoiLuong = a.khoiLuong;
                        if (updated.khoiLuongMoiDay > 0) {
                            khoiLuong = soDayAlloc * updated.khoiLuongMoiDay;
                            // Chỉ dòng được chọn chứa số dư mới cộng thêm
                            if (a.isDongChuaSoDu && soDu > 0) {
                                khoiLuong += soDu;
                            }
                        }
                        return { ...a, khoiLuong };
                    });
                } else if (field === 'soDu') {
                    // SỐ DƯ (cột ảo) - người dùng nhập
                    const soDu = parseNumber(value);
                    updated.soDu = soDu;
                    // Tính lại khối lượng mỗi dây
                    const thucNhanBBA = parseNumber(updated.thucNhanBBA);
                    const soDay = parseNumber(updated.soDay);
                    if (soDay > 0) {
                        updated.khoiLuongMoiDay = Math.max(0, (thucNhanBBA - soDu) / soDay);
                    }

                    // Recalculate tất cả allocations
                    updated.allocations = updated.allocations.map((a) => {
                        const soDayAlloc = parseNumber(a.soDay);
                        let khoiLuong = soDayAlloc * updated.khoiLuongMoiDay;
                        // Chỉ dòng được chọn chứa số dư mới cộng thêm
                        if (a.isDongChuaSoDu && soDu > 0) {
                            khoiLuong += soDu;
                        }
                        return { ...a, khoiLuong };
                    });
                } else if (field === 'soDay') {
                    // SỐ DÂY - người dùng nhập
                    const soDay = parseNumber(value);
                    updated.soDay = soDay;
                    // Tính khối lượng mỗi dây = (THỰC NHẬN BBA - SỐ DƯ) / SỐ DÂY
                    // Nếu Số dây = 0, thì khối lượng mỗi dây = 0 (sẽ dùng KG nhập trực tiếp)
                    const thucNhanBBA = parseNumber(updated.thucNhanBBA);
                    const soDu = parseNumber(updated.soDu);
                    if (soDay > 0) {
                        updated.khoiLuongMoiDay = Math.max(0, (thucNhanBBA - soDu) / soDay);
                    } else {
                        updated.khoiLuongMoiDay = 0;
                    }

                    // Recalculate tất cả allocations
                    updated.allocations = updated.allocations.map((a) => {
                        const soDayAlloc = parseNumber(a.soDay);
                        let khoiLuong = soDayAlloc * updated.khoiLuongMoiDay;
                        // Chỉ dòng được chọn chứa số dư mới cộng thêm
                        if (a.isDongChuaSoDu && soDu > 0) {
                            khoiLuong += soDu;
                        }
                        // Nếu số dây = 0, giữ nguyên khoiLuong (do user nhập trực tiếp)
                        return { ...a, soDay: a.soDay, khoiLuong };
                    });
                }

                return updated;
            })
        );
    };

    const addVehicleRow = () => {
        setVehicleRows(prev => {
            // Nếu có xe trước đó, lấy thông tin từ xe cuối cùng
            if (prev.length > 0) {
                const lastVehicle = prev[prev.length - 1];
                return [...prev, {
                    ...createEmptyVehicleRow(),
                    ncc: lastVehicle.ncc,      // Lấy lại NCC
                    soLo: lastVehicle.soLo,    // Lấy lại Số lô
                }];
            }
            // Nếu chưa có xe nào, tạo xe trống mới
            return [...prev, createEmptyVehicleRow()];
        });
    };

    const removeVehicleRow = (tempId) => {
        setVehicleRows(prev => prev.filter(r => r.tempId !== tempId));
    };

    // ====== ALLOCATIONS PER VEHICLE ======
    const addAllocationForVehicle = (tempId, teamName) => {
        setVehicleRows(prev =>
            prev.map(row => {
                if (row.tempId !== tempId) return row;
                const existed = row.allocations.find(a => a.teamName === teamName);
                if (existed) {
                    toast.warning(`Tổ ${teamName} đã có trong xe ${row.soXe || ''}`);
                    return row;
                }
                return {
                    ...row,
                    allocations: [
                        ...row.allocations,
                        { id: generateUniqueId(), teamName, soDay: '', khoiLuong: 0, isDongChuaSoDu: false }
                    ]
                };
            })
        );
    };

    // Chọn dòng chứa số dư
    const toggleDongChuaSoDu = (tempId, allocId) => {
        setVehicleRows(prev =>
            prev.map(row => {
                if (row.tempId !== tempId) return row;
                const soDu = parseNumber(row.soDu);
                const khoiLuongMoiDay = parseNumber(row.khoiLuongMoiDay);

                // Toggle isDongChuaSoDu và recalculate khối lượng
                const newAlloc = row.allocations.map(a => {
                    const soDayAlloc = parseNumber(a.soDay);
                    // Chỉ tính lại nếu có khối lượng mỗi dây (số dây > 0)
                    let khoiLuong = a.khoiLuong;
                    if (khoiLuongMoiDay > 0) {
                        khoiLuong = soDayAlloc * khoiLuongMoiDay;
                    }

                    if (a.id === allocId) {
                        const newIsDong = !a.isDongChuaSoDu;
                        if (khoiLuongMoiDay > 0 && newIsDong && soDu > 0) {
                            khoiLuong += soDu;
                        }
                        return { ...a, isDongChuaSoDu: newIsDong, khoiLuong };
                    } else {
                        // Bỏ flag isDongChuaSoDu của các dòng khác
                        return { ...a, isDongChuaSoDu: false, khoiLuong };
                    }
                });

                return { ...row, allocations: newAlloc };
            })
        );
    };

    const updateAllocationField = (tempId, allocId, field, value) => {
        setVehicleRows(prev =>
            prev.map(row => {
                if (row.tempId !== tempId) return row;

                const newAlloc = row.allocations.map((a) => {
                    if (a.id !== allocId) return a;

                    if (field === 'soDay') {
                        const soDayValue = parseNumber(value);
                        const khoiLuongMoiDay = parseNumber(row.khoiLuongMoiDay);
                        const soDu = parseNumber(row.soDu);

                        // Nếu khối lượng mỗi dây = 0 (không có Số dây chính), giữ nguyên khoiLuong
                        // Nếu có khối lượng mỗi dây, tính = số dây × khối lượng mỗi dây
                        let khoiLuong = a.khoiLuong;
                        if (khoiLuongMoiDay > 0) {
                            khoiLuong = soDayValue * khoiLuongMoiDay;
                            // Nếu dòng này được chọn chứa số dư, cộng thêm
                            if (a.isDongChuaSoDu && soDu > 0) {
                                khoiLuong += soDu;
                            }
                        }

                        return { ...a, soDay: value, khoiLuong };
                    }

                    if (field === 'khoiLuong')
                        return { ...a, khoiLuong: parseNumber(value) };

                    return a;
                });

                return { ...row, allocations: newAlloc };
            })
        );
    };

    const removeAllocationForVehicle = (tempId, allocId) => {
        setVehicleRows(prev =>
            prev.map(row =>
                row.tempId === tempId
                    ? { ...row, allocations: row.allocations.filter(a => a.id !== allocId) }
                    : row
            )
        );
    };

    // ====== VALIDATE NHIỀU XE ======
    const validateMultiVehicleForm = () => {
        const errors = [];

        if (!currentDate) {
            errors.push('Ngày tháng không được để trống');
        }

        if (vehicleRows.length === 0) {
            errors.push('Cần nhập ít nhất 1 xe');
        }

        vehicleRows.forEach((row, index) => {
            const idx = index + 1;
            if (!row.soXe.trim()) errors.push(`Xe ${idx}: Số xe không được để trống`);
            if (!row.ncc.trim()) errors.push(`Xe ${idx}: NCC không được để trống`);
            if (!row.soLo || !row.soLo.trim()) errors.push(`Xe ${idx}: Số lô không được để trống`);

            const nhap = parseNumber(row.nhapBB);
            const tru = parseNumber(row.truBB);
            const tra = parseNumber(row.traBB);
            const thucNhanBBE = parseNumber(row.thucNhanBBE);
            const thucNhanBBA = parseNumber(row.thucNhanBBA);
            const soDay = parseNumber(row.soDay);

            if (nhap <= 0) errors.push(`Xe ${idx}: Nhập BB (KG) phải lớn hơn 0`);
            if (tru + tra > nhap)
                errors.push(`Xe ${idx}: Trừ BB + Trả BB (KG) không được lớn hơn Nhập BB`);

            // Validate các field mới
            if (thucNhanBBE < 0) errors.push(`Xe ${idx}: Thực nhận BBE (KG) không hợp lệ`);

            const totalAlloc = row.allocations.reduce(
                (s, a) => s + parseNumber(a.khoiLuong),
                0
            );

            // Kiểm tra tổng khối lượng phân bổ phải = THỰC NHẬN BBA
            if (row.allocations.length > 0 && Math.abs(totalAlloc - thucNhanBBA) > 0.01) {
                errors.push(`Xe ${idx}: Tổng khối lượng phân bổ (${formatNumber(totalAlloc)}) phải bằng THỰC NHẬN BBA (${formatNumber(thucNhanBBA)})`);
            }

            // Kiểm tra tổng số dây phân bổ - chỉ khi có số dây
            if (soDay > 0) {
                const totalSoDay = row.allocations.reduce(
                    (s, a) => s + parseNumber(a.soDay),
                    0
                );
                // Chỉ check nếu có ít nhất một tổ nhập Số dây
                if (totalSoDay > 0 && Math.abs(totalSoDay - soDay) > 0.01) {
                    errors.push(`Xe ${idx}: Tổng số dây phân bổ (${formatNumber(totalSoDay)}) phải bằng số dây (${formatNumber(soDay)})`);
                }
            }

            row.allocations.forEach(a => {
                if (parseNumber(a.khoiLuong) <= 0) {
                    errors.push(`Xe ${idx} - Tổ ${a.teamName}: Khối lượng phải > 0`);
                }
                // Không bắt buộc Số dây - có thể chỉ nhập KG
                // (nếu có Số dây thì tổng phải = Số dây chính)
            });
        });

        return errors;
    };

    // ====== LƯU NHIỀU XE ======
    const handleSaveMulti = async () => {
        if (isSubmitting) return;

        const errors = validateMultiVehicleForm();
        if (errors.length > 0) {
            toast.error(errors.join('\n'));
            return;
        }

        try {
            setIsSubmitting(true);

            const dateStr = formatDateForInput(currentDate);

            for (const row of vehicleRows) {
                const nhap = parseNumber(row.nhapBB);
                const tru = parseNumber(row.truBB);
                const tra = parseNumber(row.traBB); // TRẢ BB (KG) - người dùng nhập
                const thuc = parseNumber(row.thucNhanBB);
                const thucNhanBBE = parseNumber(row.thucNhanBBE); // Người dùng nhập
                const thucNhanBBA = parseNumber(row.thucNhanBBA); // Tự động tính
                const soDay = parseNumber(row.soDay); // Người dùng nhập

                const totalPhanBo = row.allocations.reduce(
                    (s, a) => s + parseNumber(a.khoiLuong),
                    0
                );

                const recordId = row.ID || generateUniqueId();

                // Thêm dấu nháy đơn (') ở đầu để ép AppSheet hiểu SỐ XE và SỐ LÔ là text
                // Điều này ngăn AppSheet tự động chuyển "93E-00186" thành "9.3E-185"
                // Dấu ' sẽ được remove khi hiển thị
                const soXeValue = row.soXe.trim();
                const soLoValue = row.soLo.trim();

                const recordData = {
                    ID: recordId,
                    'NGÀY THÁNG': dateStr,
                    'SỐ XE': soXeValue ? `'${soXeValue}` : '',
                    'NCC': row.ncc.trim(),
                    'SỐ LÔ': soLoValue ? `'${soLoValue}` : '',
                    'NHẬP BB (KG)': nhap,
                    'TRỪ BB (KG)': tru,
                    'TRẢ BB (KG)': tra,
                    'THỰC NHẬN BB (KG)': thuc,
                    'THỰC NHẬN BBA (KG)': thucNhanBBA,
                    'THỰC NHẬN BBE (KG)': thucNhanBBE,
                    'SỐ DÂY': soDay,
                    'GHI CHÚ': row.ghiChu.trim()
                };

                // Save NHAPBAOBI
                if (row.ID) {
                    await authUtils.apiRequest('NHAPBAOBI', 'Edit', { Rows: [recordData] });
                } else {
                    await authUtils.apiRequest('NHAPBAOBI', 'Add', { Rows: [recordData] });
                }

                // Xóa phân bổ cũ và NHAPBAOBI_CT tương ứng
                if (row.ID) {
                    const oldReports = detailReports.filter(d => d.ID_NHAPBAOBI === row.ID);
                    if (oldReports.length > 0) {
                        // Xóa NHAPBAOBI_CT cũ theo IDPB_REF trước
                        const idpbList = oldReports.map(d => d.IDPB);
                        const nhapBaoBiCTResponse = await authUtils.apiRequest('NHAPBAOBI_CT', 'Find', {});
                        const nhapBaoBiCTToDelete = nhapBaoBiCTResponse.filter(ct => idpbList.includes(ct.IDPB_REF));
                        if (nhapBaoBiCTToDelete.length > 0) {
                            await authUtils.apiRequest('NHAPBAOBI_CT', 'Delete', {
                                Rows: nhapBaoBiCTToDelete.map(ct => ({ ID_CT: ct.ID_CT }))
                            });
                        }

                        // Xóa PHANBOBAOBI
                        await authUtils.apiRequest('PHANBOBAOBI', 'Delete', {
                            Rows: oldReports.map(d => ({ IDPB: d.IDPB }))
                        });
                    }
                }

                // Thêm phân bổ mới
                const validAlloc = row.allocations.filter(a => parseNumber(a.khoiLuong) > 0);
                if (validAlloc.length > 0) {
                    const soDu = parseNumber(row.soDu);

                    // Tạo IDPB trước để dùng cho cả PHANBOBAOBI và NHAPBAOBI_CT
                    const allocWithIds = validAlloc.map(a => ({
                        ...a,
                        generatedIdpb: typeof a.id === 'string' && a.id.length > 10
                            ? a.id
                            : generateUniqueId()
                    }));

                    const phanBoData = allocWithIds.map((a) => {
                        // Nếu dòng này được chọn chứa số dư, ghi "X + SỐ DƯ KG"
                        let soDayDisplay = a.soDay || '';
                        if (a.isDongChuaSoDu && soDu > 0) {
                            soDayDisplay = `${a.soDay} + ${soDu} KG`;
                        }

                        return {
                            IDPB: a.generatedIdpb,
                            ID_NHAPBAOBI: recordId,
                            'NGÀY': dateStr,
                            'TỔ': a.teamName,
                            'TÊN HÀNG': 'Bao bì anh',
                            'SỐ DÂY': soDayDisplay,
                            'KHỐI LƯỢNG': parseNumber(a.khoiLuong)
                        };
                    });
                    await authUtils.apiRequest('PHANBOBAOBI', 'Add', { Rows: phanBoData });

                    const nhapBaoBiCTData = allocWithIds.map(a => ({
                        ID_CT: generateUniqueId(),
                        IDPB_REF: a.generatedIdpb,
                        IDCBB_REF: '',
                        NGÀY: dateStr,
                        'TÊN HÀNG': 'Bao bì anh',
                        'NGHIỆP VỤ': 'Nhập',
                        'TỔ': a.teamName,
                        'SỐ XE': soXeValue ? `'${soXeValue}` : '',
                        'NCC': row.ncc.trim(),
                        'SỐ DÂY': a.soDay || '',
                        'KHỐI LƯỢNG (KG)': parseNumber(a.khoiLuong)
                    }));
                    await authUtils.apiRequest('NHAPBAOBI_CT', 'Add', {
                        Rows: nhapBaoBiCTData
                    });
                }
            }

            toast.success('Lưu thông tin cho các xe trong ngày thành công!');
            await fetchPackagingData();
            await fetchPhanBoData();
            await fetchAutoCompleteOptions();
            handleClose();
        } catch (err) {
            console.error(err);
            toast.error('Có lỗi khi lưu dữ liệu nhiều xe');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Delete / bulk delete / export giữ nguyên logic cũ
    const handleDelete = (ID, event) => {
        event.stopPropagation();
        event.preventDefault();

        setConfirmTitle("Xóa thông tin nhập bao bì");
        setConfirmMessage("Bạn có chắc chắn muốn xóa thông tin này? Các phân bổ liên quan cũng sẽ bị xóa.");
        setConfirmAction(() => async () => {
            try {
                const relatedReports = detailReports.filter(r => r.ID_NHAPBAOBI === ID);
                if (relatedReports.length > 0) {
                    // Xóa NHAPBAOBI_CT theo IDPB_REF trước
                    const idpbList = relatedReports.map(r => r.IDPB);
                    const nhapBaoBiCTResponse = await authUtils.apiRequest('NHAPBAOBI_CT', 'Find', {});
                    const nhapBaoBiCTToDelete = nhapBaoBiCTResponse.filter(ct => idpbList.includes(ct.IDPB_REF));
                    if (nhapBaoBiCTToDelete.length > 0) {
                        await authUtils.apiRequest('NHAPBAOBI_CT', 'Delete', {
                            "Rows": nhapBaoBiCTToDelete.map(ct => ({ ID_CT: ct.ID_CT }))
                        });
                    }

                    // Xóa PHANBOBAOBI
                    await authUtils.apiRequest('PHANBOBAOBI', 'Delete', {
                        "Rows": relatedReports.map(r => ({ IDPB: r.IDPB }))
                    });
                }

                await authUtils.apiRequest('NHAPBAOBI', 'Delete', {
                    "Rows": [{ "ID": ID }]
                });

                toast.success('Xóa thông tin thành công!');
                await fetchPackagingData();
                await fetchPhanBoData();
            } catch (error) {
                console.error('Error deleting record:', error);
                toast.error('Có lỗi xảy ra khi xóa thông tin');
            }
        });
        setShowConfirmModal(true);
    };

    const handleEdit = (record, event) => {
        event.stopPropagation();
        event.preventDefault();
        handleOpen(record);
    };

    const handleBulkDelete = () => {
        if (selectedRecords.length === 0) {
            toast.warning('Vui lòng chọn thông tin để xóa');
            return;
        }

        setConfirmTitle("Xóa nhiều thông tin");
        setConfirmMessage(`Bạn có chắc chắn muốn xóa ${selectedRecords.length} thông tin đã chọn?`);
        setConfirmAction(() => async () => {
            const toastId = toast.info('Đang xóa...', { autoClose: false });
            let successCount = 0;
            let errorCount = 0;

            try {
                // Lấy tất cả NHAPBAOBI_CT một lần để tối ưu
                const nhapBaoBiCTResponse = await authUtils.apiRequest('NHAPBAOBI_CT', 'Find', {});

                for (let i = 0; i < selectedRecords.length; i++) {
                    try {
                        const relatedReports = detailReports.filter(r => r.ID_NHAPBAOBI === selectedRecords[i]);
                        if (relatedReports.length > 0) {
                            // Xóa NHAPBAOBI_CT theo IDPB_REF trước
                            const idpbList = relatedReports.map(r => r.IDPB);
                            const nhapBaoBiCTToDelete = nhapBaoBiCTResponse.filter(ct => idpbList.includes(ct.IDPB_REF));
                            if (nhapBaoBiCTToDelete.length > 0) {
                                await authUtils.apiRequest('NHAPBAOBI_CT', 'Delete', {
                                    "Rows": nhapBaoBiCTToDelete.map(ct => ({ ID_CT: ct.ID_CT }))
                                });
                            }

                            // Xóa PHANBOBAOBI
                            await authUtils.apiRequest('PHANBOBAOBI', 'Delete', {
                                "Rows": relatedReports.map(r => ({ IDPB: r.IDPB }))
                            });
                        }

                        await authUtils.apiRequest('NHAPBAOBI', 'Delete', {
                            "Rows": [{ "ID": selectedRecords[i] }]
                        });
                        successCount++;
                        toast.update(toastId, {
                            render: `Đang xóa ${i + 1}/${selectedRecords.length}...`
                        });
                    } catch (error) {
                        errorCount++;
                        console.error(`Error deleting ${selectedRecords[i]}:`, error);
                    }
                }

                toast.dismiss(toastId);

                if (errorCount === 0) {
                    toast.success(`Xóa thành công ${successCount} bản ghi!`);
                } else {
                    toast.warning(`Xóa thành công ${successCount}, thất bại ${errorCount} bản ghi`);
                }

                setSelectedRecords([]);
                await fetchPackagingData();
                await fetchPhanBoData();
            } catch (error) {
                toast.dismiss(toastId);
                toast.error('Có lỗi xảy ra khi xóa thông tin');
            }
        });
        setShowConfirmModal(true);
    };

    // Helper function để format worksheet - đảm bảo cột SỐ XE, SỐ LÔ được format thành text
    const formatWorksheetTextColumns = (ws) => {
        if (!ws['!ref']) return;

        const range = XLSX.utils.decode_range(ws['!ref']);

        // Tìm vị trí cột "SỐ XE", "SỐ LÔ", "Số xe", "Số lô" trong header (row 0)
        const textColumns = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
            const cell = ws[cellAddress];
            if (cell && cell.v) {
                const headerValue = String(cell.v).trim();
                if (headerValue === 'SỐ XE' || headerValue === 'SỐ LÔ' ||
                    headerValue === 'Số xe' || headerValue === 'Số lô') {
                    textColumns.push(C);
                }
            }
        }

        // Format các cell trong các cột text thành dạng text
        textColumns.forEach(colIndex => {
            for (let R = range.s.r + 1; R <= range.e.r; ++R) {
                const cellAddress = XLSX.utils.encode_cell({ r: R, c: colIndex });
                if (ws[cellAddress]) {
                    ws[cellAddress].t = 's'; // Set cell type thành string
                    ws[cellAddress].z = '@'; // Set number format thành text
                }
            }
        });
    };

    const handleExportSelected = () => {
        if (selectedRecords.length === 0) {
            toast.warning('Vui lòng chọn thông tin để xuất file');
            return;
        }

        const selectedItems = packagingData.filter(r => selectedRecords.includes(r.ID));
        const excelData = selectedItems.map(item => ({
            ID: item.ID,
            'NGÀY THÁNG': item['NGÀY THÁNG'],
            'SỐ XE': cleanTextValue(item['SỐ XE']),
            'NCC': item['NCC'],
            'SỐ LÔ': cleanTextValue(item['SỐ LÔ']),
            'NHẬP BB (KG)': parseNumber(item['NHẬP BB (KG)']),
            'TRỪ BB (KG)': parseNumber(item['TRỪ BB (KG)']),
            'TRẢ BB (KG)': parseNumber(item['TRẢ BB (KG)']),
            'THỰC NHẬN BB (KG)': parseNumber(item['THỰC NHẬN BB (KG)']),
            'THỰC NHẬN BBA (KG)': parseNumber(item['THỰC NHẬN BBA (KG)']),
            'THỰC NHẬN BBE (KG)': parseNumber(item['THỰC NHẬN BBE (KG)']),
            'GHI CHÚ': item['GHI CHÚ']
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        formatWorksheetTextColumns(ws); // Format cột SỐ XE và SỐ LÔ thành text
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Nhập Bao Bì');
        XLSX.writeFile(wb, `nhap-bao-bi-${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    // ====== FILTERING / GROUPING ======
    const filteredData = useMemo(() => {
        let filtered = packagingData.filter(record => {
            const matchesSearch =
                cleanTextValue(record['SỐ XE']).toLowerCase().includes(search.toLowerCase()) ||
                String(record['NCC'] || '').toLowerCase().includes(search.toLowerCase()) ||
                cleanTextValue(record['SỐ LÔ']).toLowerCase().includes(search.toLowerCase()) ||
                String(record.ID || '').toLowerCase().includes(search.toLowerCase());

            const matchesSearchFilter = !filters.searchFilter ||
                cleanTextValue(record['SỐ XE']).toLowerCase().includes(filters.searchFilter.toLowerCase()) ||
                String(record['NCC'] || '').toLowerCase().includes(filters.searchFilter.toLowerCase()) ||
                cleanTextValue(record['SỐ LÔ']).toLowerCase().includes(filters.searchFilter.toLowerCase());

            let dateMatches = true;
            if (filters.startDate || filters.endDate) {
                const recordDateStr = formatDateForInput(record['NGÀY THÁNG']);
                if (!recordDateStr) {
                    dateMatches = false;
                } else {
                    const recordDate = new Date(recordDateStr);
                    recordDate.setHours(0, 0, 0, 0);

                    if (filters.startDate && filters.endDate) {
                        const startDate = new Date(filters.startDate);
                        startDate.setHours(0, 0, 0, 0);
                        const endDate = new Date(filters.endDate);
                        endDate.setHours(23, 59, 59, 999);

                        dateMatches = recordDate >= startDate && recordDate <= endDate;
                    } else if (filters.startDate) {
                        const startDate = new Date(filters.startDate);
                        startDate.setHours(0, 0, 0, 0);
                        dateMatches = recordDate >= startDate;
                    } else if (filters.endDate) {
                        const endDate = new Date(filters.endDate);
                        endDate.setHours(23, 59, 59, 999);
                        dateMatches = recordDate <= endDate;
                    }
                }
            }

            return matchesSearch && matchesSearchFilter && dateMatches;
        });

        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (sortConfig.key === 'NGÀY THÁNG') {
                    aValue = new Date(aValue);
                    bValue = new Date(bValue);
                }

                if (sortConfig.key.includes('(KG)')) {
                    aValue = parseNumber(aValue);
                    bValue = parseNumber(bValue);
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'asc' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'asc' ? 1 : -1;
                }
                return 0;
            });
        } else {
            filtered.sort((a, b) => {
                const rowA = parseInt(a._RowNumber) || 0;
                const rowB = parseInt(b._RowNumber) || 0;
                return rowB - rowA;
            });
        }

        return filtered;
    }, [packagingData, detailReports, search, filters, sortConfig]);

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const currentItems = useMemo(() => {
        const indexOfLastItem = currentPage * itemsPerPage;
        const indexOfFirstItem = indexOfLastItem - itemsPerPage;
        return filteredData.slice(indexOfFirstItem, indexOfLastItem);
    }, [filteredData, currentPage, itemsPerPage]);

    const groupedRecords = useMemo(() => {
        if (viewMode !== 'grouped') return null;
        const groups = {};
        filteredData.forEach(record => {
            const date = new Date(record['NGÀY THÁNG']).toLocaleDateString('vi-VN');
            if (!groups[date]) {
                groups[date] = [];
            }
            groups[date].push(record);
        });

        return Object.keys(groups)
            .sort((a, b) => {
                const dateA = parseVNDate(a);
                const dateB = parseVNDate(b);
                return dateB - dateA;
            })
            .map(date => {
                const records = groups[date];
                let totalAllocatedBBA = 0;
                records.forEach(record => {
                    const status = getDistributionStatus(record);
                    totalAllocatedBBA += status.totalAllocated;
                });

                const totalNhapBB = records.reduce((sum, r) => sum + parseNumber(r['NHẬP BB (KG)']) - parseNumber(r['TRẢ BB (KG)']), 0);
                const totalThucNhanBB = records.reduce((sum, r) => sum + parseNumber(r['THỰC NHẬN BB (KG)']), 0);
                const totalThucNhanBBA = records.reduce((sum, r) => sum + parseNumber(r['THỰC NHẬN BBA (KG)']), 0);
                const totalThucNhanBBE = records.reduce((sum, r) => sum + parseNumber(r['THỰC NHẬN BBE (KG)']), 0);

                return {
                    date,
                    records,
                    totalRecords: records.length,
                    totalNhapBB,
                    totalThucNhanBB,
                    totalThucNhanBBA,
                    totalThucNhanBBE,
                    totalAllocatedBBA
                };
            });
    }, [filteredData, viewMode]);

    return (
        <div className="p-3 bg-gray-50 min-h-screen">
            <div className="mx-auto">
                <div className="bg-white rounded-xl shadow-sm p-5 mb-6 border border-gray-100">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <Package className="h-6 w-6 text-indigo-600" />
                            Quản lý Nhập Bao Bì
                        </h1>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Filter className="w-4 h-4" />
                                {showFilters ? "Ẩn bộ lọc" : "Bộ lọc"}
                            </button>

                            <button
                                onClick={() => setShowExportExcelModal(true)}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Download className="w-4 h-4" />
                                Xuất BC nhập BB
                            </button>

                            <button
                                onClick={() => setShowPhanBoEmTongTheModal(true)}
                                className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Package className="w-4 h-4" />
                                Phân bổ BBE
                            </button>

                            <button
                                onClick={() => setShowChuyenBaoBiAnhSangEmModal(true)}
                                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <ArrowRight className="w-4 h-4" />
                                Chuyển BB
                            </button>

                            {selectedRecords.length > 0 && (
                                <>
                                    <button
                                        onClick={handleExportSelected}
                                        className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 transition-colors shadow-sm"
                                    >
                                        <Download className="w-4 h-4" />
                                        Xuất file ({selectedRecords.length})
                                    </button>
                                    <button
                                        onClick={handleBulkDelete}
                                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2 transition-colors shadow-sm"
                                    >
                                        <Trash className="w-4 h-4" />
                                        Xóa ({selectedRecords.length})
                                    </button>
                                </>
                            )}

                            <button
                                onClick={() => handleOpen()}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Plus className="w-4 h-4" />
                                Nhập BB & Phân bổ BBA
                            </button>

                            <button
                                onClick={() => setViewMode(viewMode === 'list' ? 'grouped' : 'list')}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                {viewMode === 'list' ? (
                                    <>
                                        <Calendar className="w-4 h-4" />
                                        Nhóm theo ngày
                                    </>
                                ) : (
                                    <>
                                        <List className="w-4 h-4" />
                                        Danh sách
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    {showFilters && (
                        <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Tìm kiếm Số xe / NCC / Số lô
                                    </label>
                                    <input
                                        type="text"
                                        value={filters.searchFilter}
                                        onChange={(e) => setFilters({ ...filters, searchFilter: e.target.value })}
                                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Nhập số xe, NCC hoặc số lô"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Từ ngày</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <Calendar className="w-4 h-4 text-gray-500" />
                                        </div>
                                        <input
                                            type="date"
                                            value={filters.startDate || ''}
                                            onChange={(e) => handleFilterDateChange('startDate', e.target.value)}
                                            className="pl-10 p-2 border rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Đến ngày</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <Calendar className="w-4 h-4 text-gray-500" />
                                        </div>
                                        <input
                                            type="date"
                                            value={filters.endDate || ''}
                                            onChange={(e) => handleFilterDateChange('endDate', e.target.value)}
                                            className="pl-10 p-2 border rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-end">
                                    <button
                                        onClick={() => setFilters({
                                            searchFilter: '',
                                            startDate: null,
                                            endDate: null
                                        })}
                                        className="w-full px-4 py-2 text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                                    >
                                        Xóa bộ lọc
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Search */}
                    <div className="mb-6">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Tìm kiếm theo ID, số xe, NCC hoặc số lô..."
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Stats */}
                    <StatisticCards data={filteredData} />

                    {/* Table */}
                    {viewMode === 'list' ? (
                        <div className="overflow-x-auto -mx-4 md:mx-0">
                            <div className="inline-block min-w-full align-middle">
                                <div className="overflow-hidden border border-gray-200 rounded-lg">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th scope="col" className="p-4 text-left">
                                                    <div className="flex items-center">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedRecords.length === filteredData.length && filteredData.length > 0}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedRecords(filteredData.map(r => r.ID));
                                                                } else {
                                                                    setSelectedRecords([]);
                                                                }
                                                            }}
                                                            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                    </div>
                                                </th>
                                                <th
                                                    scope="col"
                                                    className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left cursor-pointer hover:bg-gray-100 select-none"
                                                    onClick={() => handleSort('NGÀY THÁNG')}
                                                >
                                                    <div className="flex items-center gap-1">
                                                        Ngày tháng
                                                        <SortIcon column="NGÀY THÁNG" />
                                                    </div>
                                                </th>
                                                <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Số xe</th>
                                                <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">NCC</th>
                                                <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Số lô</th>
                                                <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left" style={{ minWidth: '200px' }}>PHÂN BỔ BBA</th>
                                                <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Nhập BB (KG)</th>
                                                <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Trừ BB (KG)</th>
                                                <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Trả BB (KG)</th>
                                                <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Thực nhận BB (KG)</th>
                                                <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Thực nhận BBA (KG)</th>
                                                <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Thực nhận BBE (KG)</th>
                                                <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Thao tác</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {currentItems.length > 0 ? (
                                                currentItems.map((record) => {
                                                    const status = getDistributionStatus(record);
                                                    return (
                                                        <React.Fragment key={record.ID}>
                                                            <tr
                                                                className="hover:bg-gray-50 transition-colors cursor-pointer"
                                                                onClick={() => handleViewDetail(record)}
                                                            >
                                                                <td className="p-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                                    <div className="flex items-center">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={selectedRecords.includes(record.ID)}
                                                                            onChange={(e) => {
                                                                                if (e.target.checked) {
                                                                                    setSelectedRecords([...selectedRecords, record.ID]);
                                                                                } else {
                                                                                    setSelectedRecords(selectedRecords.filter(id => id !== record.ID));
                                                                                }
                                                                            }}
                                                                            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                                        />
                                                                    </div>
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700">
                                                                    {new Date(record['NGÀY THÁNG']).toLocaleDateString('vi-VN')}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium">{cleanTextValue(record['SỐ XE'])}</td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700">{record['NCC']}</td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-purple-600">{cleanTextValue(record['SỐ LÔ'])}</td>
                                                                <td className="px-3 py-2.5 w-64 align-top">
                                                                    <AllocationSummary record={record} />
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-blue-600">
                                                                    {formatNumber(record['NHẬP BB (KG)'])}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-red-600">
                                                                    {formatNumber(record['TRỪ BB (KG)'])}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-yellow-600">
                                                                    {formatNumber(record['TRẢ BB (KG)'])}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-green-600">
                                                                    {formatNumber(record['THỰC NHẬN BB (KG)'])}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-teal-600">
                                                                    {formatNumber(record['THỰC NHẬN BBA (KG)'])}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-pink-600">
                                                                    {formatNumber(record['THỰC NHẬN BBE (KG)'])}
                                                                </td>

                                                                <td className="px-3 py-2.5 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                                                                    <div className="flex justify-end space-x-1">
                                                                        {status.hasAllocation && (
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleDetailExpansion(record.ID);
                                                                                }}
                                                                                className="text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50"
                                                                            >
                                                                                {expandedDetailGroups.has(record.ID) ?
                                                                                    <ChevronUp className="h-4 w-4" /> :
                                                                                    <ChevronDown className="h-4 w-4" />
                                                                                }
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            onClick={(e) => handleEdit(record, e)}
                                                                            className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-full hover:bg-indigo-50"
                                                                            title="Sửa thông tin"
                                                                        >
                                                                            <Edit className="h-4 w-4" />
                                                                        </button>
                                                                        <button
                                                                            onClick={(e) => handleDelete(record.ID, e)}
                                                                            className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"
                                                                            title="Xóa thông tin"
                                                                        >
                                                                            <Trash className="h-4 w-4" />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>

                                                            {expandedDetailGroups.has(record.ID) && status.hasAllocation && (
                                                                <DetailReportsSection record={record} />
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="12" className="px-4 py-6 text-center text-sm text-gray-500">
                                                        Không tìm thấy thông tin nào phù hợp với tiêu chí tìm kiếm
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {groupedRecords && groupedRecords.map(group => (
                                <div key={group.date} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                                    <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
                                        <div>
                                            <h3 className="text-lg font-medium text-gray-800">
                                                Ngày: {group.date}
                                            </h3>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                                                    Tổng: {group.totalRecords} xe
                                                </span>
                                                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-green-100 text-green-800">
                                                    Nhập BB: {formatNumber(group.totalNhapBB)} KG
                                                </span>
                                                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-green-200 text-green-900">
                                                    Thực nhận BB: {formatNumber(group.totalThucNhanBB)} KG
                                                </span>
                                                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-teal-100 text-teal-800">
                                                    Thực nhận BBA: {formatNumber(group.totalThucNhanBBA)} KG
                                                </span>
                                                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-indigo-100 text-indigo-800">
                                                    Đã phân bổ BBA: {formatNumber(group.totalAllocatedBBA)} KG
                                                </span>
                                                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-pink-100 text-pink-800">
                                                    Thực nhận BBE: {formatNumber(group.totalThucNhanBBE)} KG
                                                </span>
                                            </div>
                                        </div>
                                        <div>
                                            <button
                                                onClick={() => {
                                                    const excelData = group.records.map(item => ({
                                                        ID: item.ID,
                                                        'NGÀY THÁNG': item['NGÀY THÁNG'],
                                                        'SỐ XE': cleanTextValue(item['SỐ XE']),
                                                        'NCC': item['NCC'],
                                                        'SỐ LÔ': cleanTextValue(item['SỐ LÔ']),
                                                        'NHẬP BB (KG)': parseNumber(item['NHẬP BB (KG)']),
                                                        'TRỪ BB (KG)': parseNumber(item['TRỪ BB (KG)']),
                                                        'TRẢ BB (KG)': parseNumber(item['TRẢ BB (KG)']),
                                                        'THỰC NHẬN BB (KG)': parseNumber(item['THỰC NHẬN BB (KG)']),
                                                        'THỰC NHẬN BBA (KG)': parseNumber(item['THỰC NHẬN BBA (KG)']),
                                                        'THỰC NHẬN BBE (KG)': parseNumber(item['THỰC NHẬN BBE (KG)']),
                                                        'GHI CHÚ': item['GHI CHÚ']
                                                    }));

                                                    const ws = XLSX.utils.json_to_sheet(excelData);
                                                    formatWorksheetTextColumns(ws); // Format cột SỐ XE và SỐ LÔ thành text
                                                    const wb = XLSX.utils.book_new();
                                                    XLSX.utils.book_append_sheet(wb, ws, 'Nhập Bao Bì');
                                                    XLSX.writeFile(wb, `nhap-bao-bi-${group.date.replace(/\//g, '-')}.xlsx`);
                                                }}
                                                className="px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-1.5 text-sm transition-colors shadow-sm"
                                            >
                                                <Download className="w-3.5 h-3.5" />
                                                Xuất file
                                            </button>
                                        </div>
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Số xe</th>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">NCC</th>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Số lô</th>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left" style={{ minWidth: '200px' }}>PHÂN BỔ BBA</th>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Nhập BB (KG)</th>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Trừ BB (KG)</th>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Trả BB (KG)</th>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Thực nhận BB (KG)</th>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Thực nhận BBA (KG)</th>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Thực nhận BBE (KG)</th>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-left">Chi tiết</th>
                                                    <th scope="col" className="px-3 py-2.5 text-xs font-medium text-gray-500 uppercase tracking-wider text-right">Thao tác</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {group.records.map(record => {
                                                    const status = getDistributionStatus(record);
                                                    return (
                                                        <React.Fragment key={record.ID}>
                                                            <tr
                                                                className="hover:bg-gray-50 transition-colors cursor-pointer"
                                                                onClick={() => handleViewDetail(record)}
                                                            >
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium">{cleanTextValue(record['SỐ XE'])}</td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700">{record['NCC']}</td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-purple-600">{cleanTextValue(record['SỐ LÔ'])}</td>
                                                                <td className="px-3 py-2.5 w-64 align-top">
                                                                    <AllocationSummary record={record} />
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-blue-600">
                                                                    {formatNumber(record['NHẬP BB (KG)'])}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-red-600">
                                                                    {formatNumber(record['TRỪ BB (KG)'])}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-yellow-600">
                                                                    {formatNumber(record['TRẢ BB (KG)'])}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-green-600">
                                                                    {formatNumber(record['THỰC NHẬN BB (KG)'])}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-teal-600">
                                                                    {formatNumber(record['THỰC NHẬN BBA (KG)'])}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-sm text-gray-700 font-medium text-pink-600">
                                                                    {formatNumber(record['THỰC NHẬN BBE (KG)'])}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                                                                    {status.hasAllocation && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                toggleGroupDetailExpansion(record.ID);
                                                                            }}
                                                                            className="text-blue-600 hover:text-blue-900 p-1.5 rounded-full hover:bg-blue-50"
                                                                        >
                                                                            {expandedGroupDetailGroups.has(record.ID) ?
                                                                                <ChevronUp className="h-4 w-4" /> :
                                                                                <ChevronDown className="h-4 w-4" />
                                                                            }
                                                                        </button>
                                                                    )}
                                                                </td>
                                                                <td className="px-3 py-2.5 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                                                                    <div className="flex justify-end space-x-1">
                                                                        <button
                                                                            onClick={(e) => handleEdit(record, e)}
                                                                            className="text-indigo-600 hover:text-indigo-900 p-1.5 rounded-full hover:bg-indigo-50"
                                                                            title="Sửa thông tin"
                                                                        >
                                                                            <Edit className="h-4 w-4" />
                                                                        </button>
                                                                        <button
                                                                            onClick={(e) => handleDelete(record.ID, e)}
                                                                            className="text-red-600 hover:text-red-900 p-1.5 rounded-full hover:bg-red-50"
                                                                            title="Xóa thông tin"
                                                                        >
                                                                            <Trash className="h-4 w-4" />
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>

                                                            {expandedGroupDetailGroups.has(record.ID) && status.hasAllocation && (
                                                                <DetailReportsSection record={record} />
                                                            )}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}

                            {groupedRecords && groupedRecords.length === 0 && (
                                <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                                    <p className="text-gray-500">Không tìm thấy thông tin nào phù hợp với tiêu chí tìm kiếm</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Pagination */}
                    {viewMode === 'list' && currentItems.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                            itemsPerPage={itemsPerPage}
                            onItemsPerPageChange={handleItemsPerPageChange}
                            totalItems={filteredData.length}
                        />
                    )}
                </div>
            </div>

            {/* Detail View Modal */}
            {showDetailModal && selectedRecord && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-xl max-w-7xl w-full max-h-[90vh] p-6 overflow-y-auto">
                        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-5">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <Eye className="h-5 w-5 text-indigo-600" />
                                Chi tiết thông tin nhập bao bì
                            </h2>
                            <button
                                onClick={handleCloseDetail}
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <div className="space-y-6">
                            {/* Basic Information */}
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Ngày tháng</label>
                                    <div className="flex items-center gap-2">
                                        <Calendar className="h-4 w-4 text-gray-500" />
                                        <span className="text-sm text-gray-900">
                                            {new Date(selectedRecord['NGÀY THÁNG']).toLocaleDateString('vi-VN')}
                                        </span>
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Số xe</label>
                                    <span className="text-sm text-gray-900 font-medium">{cleanTextValue(selectedRecord['SỐ XE'])}</span>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">NCC</label>
                                    <span className="text-sm text-gray-900">{selectedRecord['NCC']}</span>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Số lô</label>
                                    <span className="text-sm text-gray-900 font-medium text-purple-600">{selectedRecord['SỐ LÔ']}</span>
                                </div>
                            </div>

                            {/* Ghi chú */}
                            {selectedRecord['GHI CHÚ'] && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Ghi chú</label>
                                    <p className="text-sm text-gray-900">{selectedRecord['GHI CHÚ']}</p>
                                </div>
                            )}

                            {/* Bao bì Information */}
                            <div className="border border-blue-200 rounded-lg p-5 bg-blue-50">
                                <h3 className="text-lg font-medium text-blue-800 mb-4 flex items-center gap-2">
                                    <Package className="w-5 h-5" />
                                    Thông tin bao bì
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                                    <div className="bg-white p-4 rounded-lg">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Nhập BB (KG)</label>
                                        <span className="text-lg font-bold text-blue-600">
                                            {formatNumber(selectedRecord['NHẬP BB (KG)'])}
                                        </span>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Trừ BB (KG)</label>
                                        <span className="text-lg font-bold text-orange-600">
                                            {formatNumber(selectedRecord['TRỪ BB (KG)'])}
                                        </span>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Trả BB (KG)</label>
                                        <span className="text-lg font-bold text-red-600">
                                            {formatNumber(selectedRecord['TRẢ BB (KG)'])}
                                        </span>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Thực nhận BB (KG)</label>
                                        <span className="text-lg font-bold text-green-600">
                                            {formatNumber(selectedRecord['THỰC NHẬN BB (KG)'])}
                                        </span>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Thực nhận BBA (KG)</label>
                                        <span className="text-lg font-bold text-teal-600">
                                            {formatNumber(selectedRecord['THỰC NHẬN BBA (KG)'])}
                                        </span>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Thực nhận BBE (KG)</label>
                                        <span className="text-lg font-bold text-pink-600">
                                            {formatNumber(selectedRecord['THỰC NHẬN BBE (KG)'])}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Chi tiết phân bổ BBA */}
                            {getDistributionStatus(selectedRecord).hasAllocation && (
                                <div className="border border-gray-200 rounded-lg p-5">
                                    <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                                        <Users className="w-5 h-5" />
                                        Chi tiết phân bổ BBA
                                    </h3>
                                    {(() => {
                                        const status = getDistributionStatus(selectedRecord);

                                        // Nhóm theo TỔ
                                        const groupedByTo = status.relatedReports.reduce((acc, report) => {
                                            const to = report['TỔ'] || 'Chưa phân tổ';

                                            if (!acc[to]) {
                                                acc[to] = [];
                                            }
                                            acc[to].push(report);
                                            return acc;
                                        }, {});

                                        return (
                                            <div className="space-y-4">
                                                {Object.entries(groupedByTo).map(([to, reports]) => {
                                                    const totalAmount = reports.reduce((sum, r) => sum + parseNumber(r['KHỐI LƯỢNG']), 0);

                                                    return (
                                                        <div key={to} className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                                                            <div className="bg-gradient-to-r from-indigo-100 to-blue-100 px-3 py-2.5 border-b border-blue-200">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="px-3 py-1 bg-indigo-600 text-white rounded-lg text-sm font-bold">
                                                                        {to}
                                                                    </span>
                                                                    <div className="text-sm font-bold text-green-700">
                                                                        Tổng: {formatNumber(totalAmount)} KG
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="overflow-x-auto">
                                                                <table className="min-w-full divide-y divide-blue-200">
                                                                    <thead className="bg-blue-50">
                                                                        <tr>
                                                                            <th className="px-3 py-2 text-left text-xs font-medium text-blue-700 uppercase">Ngày</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-medium text-blue-700 uppercase">Tên hàng</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-medium text-blue-700 uppercase">Số dây</th>
                                                                            <th className="px-3 py-2 text-left text-xs font-medium text-blue-700 uppercase">Khối lượng</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="bg-white divide-y divide-blue-100">
                                                                        {reports.map((report, index) => (
                                                                            <tr key={report.IDPB} className="hover:bg-blue-50">
                                                                                <td className="px-3 py-2 text-sm">
                                                                                    {new Date(report['NGÀY']).toLocaleDateString('vi-VN')}
                                                                                </td>
                                                                                <td className="px-3 py-2 text-sm font-medium">
                                                                                    {report['TÊN HÀNG']}
                                                                                </td>
                                                                                <td className="px-3 py-2 text-sm text-blue-600">
                                                                                    {report['SỐ DÂY'] || '-'}
                                                                                </td>
                                                                                <td className="px-3 py-2 text-sm font-bold text-green-700">
                                                                                    {formatNumber(report['KHỐI LƯỢNG'])} KG
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
                            <button
                                onClick={handleCloseDetail}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
                            >
                                Đóng
                            </button>
                            <button
                                onClick={(e) => {
                                    handleCloseDetail();
                                    handleEdit(selectedRecord, e);
                                }}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 transition-colors shadow-sm"
                            >
                                <Edit className="w-4 h-4" />
                                Chỉnh sửa
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ====== MODAL NHẬP NHIỀU XE ====== */}
            {/* ====== MODAL NHẬP NHIỀU XE (UI MỚI THEO THẺ) ====== */}
            {open && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] flex flex-col">
                        {/* Header */}
                        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                        <Package className="h-6 w-6 text-indigo-600" />
                                        Nhập bao bì theo ngày – nhiều xe
                                    </h2>
                                </div>
                                <button
                                    onClick={handleClose}
                                    className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {/* Chọn ngày */}
                                <div className="mt-3">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Ngày tháng <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative max-w-xs">
                                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <Calendar className="w-4 h-4 text-gray-500" />
                                        </div>
                                        <input
                                            type="date"
                                            value={formatDateForInput(currentDate)}
                                            onChange={(e) => {
                                                setCurrentDate(e.target.value);
                                            }}
                                            className="pl-10 p-2.5 border border-gray-300 rounded-lg w-full focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                                        />
                                    </div>
                                </div>
                                <div className="mt-9">
                                    <button
                                        onClick={() => setShowImportFullModal(true)}
                                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2 transition-colors shadow-sm"
                                    >
                                        <Upload className="w-4 h-4" />
                                        Nhập Excel BB và phân bổ BBA
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="px-6 pt-4 pb-6 space-y-4 overflow-y-auto">
                            {vehicleRows.map((row, index) => {
                                const totalAlloc = row.allocations.reduce(
                                    (s, a) => s + parseNumber(a.khoiLuong),
                                    0
                                );
                                const remaining = row.thucNhanBB - totalAlloc;

                                return (
                                    <div
                                        key={row.tempId}
                                        className="border border-gray-200 rounded-2xl bg-gray-50 p-4 relative shadow-sm"
                                    >
                                        {/* Tiêu đề thẻ xe */}
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex items-center gap-2">
                                                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-600 text-white text-sm font-bold">
                                                    {index + 1}
                                                </span>
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-800">
                                                        Xe {row.soXe || '(chưa nhập)'}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        NCC: {row.ncc || '—'} • Số lô: {row.soLo || '—'}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeVehicleRow(row.tempId)}
                                                className="text-red-500 hover:text-red-700 p-1.5 rounded-full hover:bg-red-50"
                                                title="Xóa xe này"
                                            >
                                                <Trash className="w-4 h-4" />
                                            </button>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1.5fr)_minmax(0,1fr)] gap-3 mb-3">
                                            {/* Số xe – cố định khoảng 200–220px, đủ cho ~12 ký tự */}
                                            <div className="md:w-[220px]">
                                                <AutocompleteInput
                                                    label="Số xe"
                                                    value={row.soXe}
                                                    onChange={(val) => updateVehicleField(row.tempId, 'soXe', val)}
                                                    options={soXeOptions}
                                                    placeholder="Ví dụ: 98L2-12345"
                                                    required
                                                />
                                            </div>

                                            {/* NCC – rộng nhất */}
                                            <div className="md:w-full">
                                                <AutocompleteInput
                                                    label="NCC"
                                                    value={row.ncc}
                                                    onChange={(val) => updateVehicleField(row.tempId, 'ncc', val)}
                                                    options={nccOptions}
                                                    placeholder="Nhập / chọn NCC"
                                                    required
                                                />
                                            </div>

                                            {/* Số lô – trung bình - LỌC THEO NCC */}
                                            <div className="md:w-full">
                                                <AutocompleteInput
                                                    label="Số lô"
                                                    value={row.soLo}
                                                    onChange={(val) => updateVehicleField(row.tempId, 'soLo', val)}
                                                    options={getSoLoOptionsByNCC(row.ncc)}
                                                    placeholder={row.ncc ? "Chọn số lô của NCC này" : "Chọn NCC trước"}
                                                    required
                                                />
                                                {/* Hiển thị thông tin Số lô */}
                                                {row.soLo && (() => {
                                                    const totalThucNhan = getTotalThucNhanBBByLo(row.soLo);
                                                    const loInfo = getLoInfo(row.soLo);
                                                    const khoiLuongDuKien = loInfo ? parseFloat(loInfo['Khối lượng dự kiến (tấn)']) || 0 : 0;
                                                    const khoiLuongDuKienKG = khoiLuongDuKien * 1000; // Chuyển từ tấn sang KG

                                                    return (
                                                        <div className="mt-1 text-xs space-y-0.5">
                                                            {loInfo && (
                                                                <div className="text-blue-600">
                                                                    <span className="font-medium">Dự kiến:</span> {khoiLuongDuKien.toFixed(2)} tấn ({formatNumber(khoiLuongDuKienKG)} KG)
                                                                </div>
                                                            )}
                                                            <div className="text-green-600 font-medium">
                                                                <span>Đã nhập:</span> {formatNumber(totalThucNhan)} KG {loInfo && `(${(totalThucNhan / 1000).toFixed(2)} tấn)`}
                                                            </div>
                                                            {loInfo && (
                                                                <div className={`font-semibold ${totalThucNhan >= khoiLuongDuKienKG ? 'text-green-700' : 'text-orange-600'}`}>
                                                                    <span>Chênh lệch:</span> {formatNumber(Math.max(0, khoiLuongDuKienKG - totalThucNhan))} KG
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        </div>


                                        {/* Hàng 3: Nhập BB / % trừ / Trừ / Thực nhận */}
                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Nhập BB (KG) <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                    value={row.nhapBB}
                                                    onChange={(e) =>
                                                        updateVehicleField(row.tempId, 'nhapBB', e.target.value)
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    % trừ bao bì
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    step="0.1"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                    value={row.percentTru}
                                                    onChange={(e) =>
                                                        updateVehicleField(row.tempId, 'percentTru', e.target.value)
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Trừ BB (KG)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                    value={row.truBB}
                                                    onChange={(e) =>
                                                        updateVehicleField(row.tempId, 'truBB', e.target.value)
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Trả BB (KG)
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                    value={row.traBB}
                                                    onChange={(e) =>
                                                        updateVehicleField(row.tempId, 'traBB', e.target.value)
                                                    }
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Thực nhận BB (KG)
                                                </label>
                                                <div className="px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm font-semibold text-green-600 flex items-center">
                                                    {formatNumber(row.thucNhanBB)} KG
                                                </div>
                                            </div>
                                        </div>

                                        {/* Hàng 4: THỰC NHẬN BBE / BBA / SỐ DƯ / SỐ DÂY / KL mỗi dây */}
                                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Thực nhận BBE (KG) <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                    value={row.thucNhanBBE}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value === '' || /^[0-9]*[,.]?[0-9]*$/.test(value)) {
                                                            updateVehicleField(row.tempId, 'thucNhanBBE', value.replace(',', '.'));
                                                        }
                                                    }}
                                                    placeholder="Nhập BBE"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Thực nhận BBA (KG)
                                                </label>
                                                <div className="px-3 py-2 border border-gray-200 rounded-lg bg-blue-50 text-sm font-semibold text-blue-600 flex items-center">
                                                    {formatNumber(row.thucNhanBBA)} KG
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Số dư (KG)
                                                </label>
                                                <input
                                                    type="text"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                    value={row.soDu}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        if (value === '' || /^[0-9]*[,.]?[0-9]*$/.test(value)) {
                                                            updateVehicleField(row.tempId, 'soDu', value.replace(',', '.'));
                                                        }
                                                    }}
                                                    placeholder="Nhập số dư"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Số dây {parseNumber(row.thucNhanBBA) > 0 && <span className="text-red-500">*</span>}
                                                </label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                                    value={row.soDay}
                                                    onChange={(e) => {
                                                        updateVehicleField(row.tempId, 'soDay', e.target.value);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (['-', '+', 'e', 'E'].includes(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    placeholder="Nhập số dây"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    KL mỗi dây (KG)
                                                </label>
                                                <div className="px-3 py-2 border border-gray-200 rounded-lg bg-purple-50 text-sm font-semibold text-purple-600 flex items-center">
                                                    {formatNumber(row.khoiLuongMoiDay)} KG
                                                </div>
                                            </div>
                                        </div>

                                        {/* Hàng 5: Ghi chú */}
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Ghi chú
                                            </label>
                                            <textarea
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                                                rows="1"
                                                placeholder="Nhập ghi chú cho xe này (nếu có)..."
                                                value={row.ghiChu}
                                                onChange={(e) =>
                                                    updateVehicleField(row.tempId, 'ghiChu', e.target.value)
                                                }
                                            />
                                        </div>

                                        {/* Hàng 5: Phân bổ BBA */}
                                        <div className="bg-white border border-green-200 rounded-xl p-3">
                                            <div className="flex justify-between items-center mb-2">
                                                <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
                                                    <Users className="w-4 h-4" />
                                                    Phân bổ BBA cho xe này
                                                </p>
                                                <div className="text-gray-600 text-sm">
                                                    Thực nhận BBA: <span className="font-semibold text-indigo-600">{formatNumber(totalAlloc)} KG</span>{' '}
                                                    • Thực nhận BBE:{' '}
                                                    <span className={`font-semibold ${remaining < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                                                        {formatNumber(remaining)} KG
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Dòng nút Tổ */}
                                            <div className="flex flex-wrap gap-1 mb-2">
                                                {teamList.map((t, teamIdx) => (
                                                    <button
                                                        key={t}
                                                        type="button"
                                                        data-vehicle={row.tempId}
                                                        data-team-idx={teamIdx}
                                                        data-team-name={t}
                                                        onClick={() => addAllocationForVehicle(row.tempId, t)}
                                                        onKeyDown={(e) => {
                                                            if (['ArrowLeft', 'ArrowRight', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
                                                                e.preventDefault();
                                                                if (e.key === 'ArrowLeft') {
                                                                    const prevBtn = document.querySelector(
                                                                        `button[data-vehicle="${row.tempId}"][data-team-idx="${teamIdx - 1}"]`
                                                                    );
                                                                    if (prevBtn) prevBtn.focus();
                                                                }
                                                                if (e.key === 'ArrowRight') {
                                                                    const nextBtn = document.querySelector(
                                                                        `button[data-vehicle="${row.tempId}"][data-team-idx="${teamIdx + 1}"]`
                                                                    );
                                                                    if (nextBtn) nextBtn.focus();
                                                                }
                                                                if (e.key === 'ArrowDown') {
                                                                    // Nhảy xuống ô Số dây của tổ cùng tên trong bảng phân bổ
                                                                    const targetInput = document.querySelector(
                                                                        `input[data-vehicle="${row.tempId}"][data-team-name="${t}"][data-alloc-col="0"]`
                                                                    ) || document.querySelector(
                                                                        `input[data-vehicle="${row.tempId}"][data-alloc-row="0"][data-alloc-col="0"]`
                                                                    );
                                                                    if (targetInput) { targetInput.focus(); targetInput.select(); }
                                                                }
                                                                if (e.key === 'ArrowUp') {
                                                                    const prevBtn = document.querySelector(
                                                                        `button[data-vehicle="${row.tempId}"][data-team-idx="${teamIdx - 1}"]`
                                                                    );
                                                                    if (prevBtn) prevBtn.focus();
                                                                }
                                                            }
                                                        }}
                                                        className="px-2.5 py-1 text-xs border border-gray-300 rounded-full bg-gray-50 hover:bg-indigo-50 hover:border-indigo-300 text-gray-700 focus:ring-2 focus:ring-indigo-400 focus:outline-none"
                                                    >
                                                        {t}
                                                    </button>
                                                ))}
                                                {teamList.length === 0 && (
                                                    <span className="text-xs text-gray-500">
                                                        Không có tổ Bao bì anh cho ngày này
                                                    </span>
                                                )}
                                            </div>

                                            {/* Bảng nhỏ chỉnh allocations */}
                                            {row.allocations.length > 0 ? (
                                                <div className="border border-indigo-100 rounded-lg p-2 bg-indigo-50/60">
                                                    <table className="w-full text-xs">
                                                        <thead>
                                                            <tr className="text-indigo-800">
                                                                <th className="text-left pb-1 w-20">Tổ</th>
                                                                <th className="text-left pb-1 w-20">Số dây</th>
                                                                <th className="text-left pb-1 w-36">Khối lượng (KG)</th>
                                                                <th className="text-center pb-1 w-24">Chứa số dư?</th>
                                                                <th className="w-8" />
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {row.allocations.map((a, allocIdx) => {
                                                                const soDu = parseNumber(row.soDu);

                                                                return (
                                                                    <tr key={a.id} className="align-middle">
                                                                        <td className="pr-2 py-1 font-semibold text-indigo-800">
                                                                            {a.teamName}
                                                                        </td>
                                                                        <td className="pr-2 py-1">
                                                                            <input
                                                                                type="number"
                                                                                step="0.01"
                                                                                min="0"
                                                                                className="w-full px-2 py-1 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                                                                                value={a.soDay}
                                                                                data-vehicle={row.tempId}
                                                                                data-alloc-row={allocIdx}
                                                                                data-alloc-col="0"
                                                                                data-team-name={a.teamName}
                                                                                onChange={(e) => {
                                                                                    updateAllocationField(
                                                                                        row.tempId,
                                                                                        a.id,
                                                                                        'soDay',
                                                                                        e.target.value
                                                                                    );
                                                                                }}
                                                                                onKeyDown={(e) => {
                                                                                    if (['-', '+', 'e', 'E'].includes(e.key)) {
                                                                                        e.preventDefault();
                                                                                    }
                                                                                    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                                                                        e.preventDefault();
                                                                                        const currentRow = allocIdx;
                                                                                        const currentCol = 0;
                                                                                        let targetRow = currentRow;
                                                                                        let targetCol = currentCol;
                                                                                        if (e.key === 'ArrowUp') targetRow = currentRow - 1;
                                                                                        if (e.key === 'ArrowDown') targetRow = currentRow + 1;
                                                                                        if (e.key === 'ArrowLeft') targetCol = currentCol - 1;
                                                                                        if (e.key === 'ArrowRight') targetCol = currentCol + 1;
                                                                                        if (targetCol < 0) targetCol = 0;
                                                                                        if (targetCol > 1) targetCol = 1;
                                                                                        // Nếu đang ở hàng đầu và nhấn lên → nhảy lên nút tổ cùng tên
                                                                                        if (e.key === 'ArrowUp' && currentRow === 0) {
                                                                                            const teamBtn = document.querySelector(
                                                                                                `button[data-vehicle="${row.tempId}"][data-team-name="${a.teamName}"]`
                                                                                            ) || document.querySelector(
                                                                                                `button[data-vehicle="${row.tempId}"][data-team-idx="0"]`
                                                                                            );
                                                                                            if (teamBtn) { teamBtn.focus(); return; }
                                                                                        }
                                                                                        const targetInput = document.querySelector(
                                                                                            `input[data-vehicle="${row.tempId}"][data-alloc-row="${targetRow}"][data-alloc-col="${targetCol}"]`
                                                                                        );
                                                                                        if (targetInput) {
                                                                                            targetInput.focus();
                                                                                            targetInput.select();
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                placeholder="Số dây"
                                                                            />
                                                                        </td>
                                                                        <td className="pr-2 py-1">
                                                                            <div className="flex items-center gap-1">
                                                                                {/* ✅ Cho phép sửa để làm tròn */}
                                                                                <input
                                                                                    type="text"
                                                                                    className="w-32 px-2 py-1 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500 text-sm font-semibold text-green-600"
                                                                                    value={a.khoiLuong}
                                                                                    data-vehicle={row.tempId}
                                                                                    data-alloc-row={allocIdx}
                                                                                    data-alloc-col="1"
                                                                                    data-team-name={a.teamName}
                                                                                    onChange={(e) => {
                                                                                        const value = e.target.value;
                                                                                        if (value === '' || /^[0-9]*[,.]?[0-9]*$/.test(value)) {
                                                                                            updateAllocationField(
                                                                                                row.tempId,
                                                                                                a.id,
                                                                                                'khoiLuong',
                                                                                                value.replace(',', '.')
                                                                                            );
                                                                                        }
                                                                                    }}
                                                                                    onKeyDown={(e) => {
                                                                                        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                                                                            e.preventDefault();
                                                                                            const currentRow = allocIdx;
                                                                                            const currentCol = 1;
                                                                                            let targetRow = currentRow;
                                                                                            let targetCol = currentCol;
                                                                                            if (e.key === 'ArrowUp') targetRow = currentRow - 1;
                                                                                            if (e.key === 'ArrowDown') targetRow = currentRow + 1;
                                                                                            if (e.key === 'ArrowLeft') targetCol = currentCol - 1;
                                                                                            if (e.key === 'ArrowRight') targetCol = currentCol + 1;
                                                                                            if (targetCol < 0) targetCol = 0;
                                                                                            if (targetCol > 1) targetCol = 1;
                                                                                            // Nếu đang ở hàng đầu và nhấn lên → nhảy lên nút tổ cùng tên
                                                                                            if (e.key === 'ArrowUp' && currentRow === 0) {
                                                                                                const teamBtn = document.querySelector(
                                                                                                    `button[data-vehicle="${row.tempId}"][data-team-name="${a.teamName}"]`
                                                                                                ) || document.querySelector(
                                                                                                    `button[data-vehicle="${row.tempId}"][data-team-idx="0"]`
                                                                                                );
                                                                                                if (teamBtn) { teamBtn.focus(); return; }
                                                                                            }
                                                                                            const targetInput = document.querySelector(
                                                                                                `input[data-vehicle="${row.tempId}"][data-alloc-row="${targetRow}"][data-alloc-col="${targetCol}"]`
                                                                                            );
                                                                                            if (targetInput) {
                                                                                                targetInput.focus();
                                                                                                targetInput.select();
                                                                                            }
                                                                                        }
                                                                                    }}
                                                                                    placeholder="KL (KG)"
                                                                                    title="Có thể sửa để làm tròn cho khớp tổng"
                                                                                />
                                                                                {a.isDongChuaSoDu && soDu > 0 && (
                                                                                    <span className="text-orange-600 text-[10px] whitespace-nowrap">
                                                                                        (+{formatNumber(soDu)})
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </td>

                                                                        <td className="py-1 text-center">
                                                                            <input
                                                                                type="radio"
                                                                                name={`soDu-${row.tempId}`}
                                                                                checked={a.isDongChuaSoDu || false}
                                                                                onChange={() => toggleDongChuaSoDu(row.tempId, a.id)}
                                                                                className="w-4 h-4 text-orange-600 focus:ring-orange-500 cursor-pointer"
                                                                                title="Chọn dòng này để chứa số dư"
                                                                            />
                                                                        </td>
                                                                        <td className="py-1 text-right">
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => removeAllocationForVehicle(row.tempId, a.id)}
                                                                                className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50"
                                                                            >
                                                                                <X className="w-3 h-3" />
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </tbody>
                                                    </table>

                                                    {/* Hiển thị cảnh báo nếu tổng không khớp */}
                                                    {(() => {
                                                        const totalSoDayPhanBo = row.allocations.reduce((s, a) => s + parseNumber(a.soDay), 0);
                                                        const totalKLPhanBo = row.allocations.reduce((s, a) => s + parseNumber(a.khoiLuong), 0);
                                                        const soDayInput = parseNumber(row.soDay);
                                                        const thucNhanBBA = parseNumber(row.thucNhanBBA);

                                                        const soDayValid = Math.abs(totalSoDayPhanBo - soDayInput) < 0.01;
                                                        const klValid = Math.abs(totalKLPhanBo - thucNhanBBA) < 0.01;

                                                        if (!soDayValid || !klValid) {
                                                            return (
                                                                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                                                    {!soDayValid && (
                                                                        <div>⚠️ Tổng số dây phân bổ ({formatNumber(totalSoDayPhanBo)}) phải bằng Số dây ({formatNumber(soDayInput)})</div>
                                                                    )}
                                                                    {!klValid && (
                                                                        <div>⚠️ Tổng khối lượng ({formatNumber(totalKLPhanBo)}) phải bằng THỰC NHẬN BBA ({formatNumber(thucNhanBBA)})</div>
                                                                    )}
                                                                </div>
                                                            );
                                                        }
                                                        return (
                                                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                                                                ✓ Tổng số dây: {formatNumber(totalSoDayPhanBo)} • Tổng KL: {formatNumber(totalKLPhanBo)} KG
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                <div className="text-xs text-gray-500 italic">
                                                    Chưa có phân bổ nào. Bấm vào tên Tổ phía trên để thêm.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Thêm xe */}
                            <button
                                type="button"
                                onClick={addVehicleRow}
                                className="w-full md:w-auto px-4 py-2 border border-dashed border-indigo-400 rounded-lg text-indigo-600 hover:bg-indigo-50 flex items-center justify-center gap-2 text-sm font-medium"
                            >
                                <Plus className="w-4 h-4" /> Thêm xe
                            </button>

                            {/* Ghi chú */}
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs md:text-sm text-yellow-800">
                                • Mỗi thẻ là 1 xe trong ngày đã chọn.
                                • Cần nhập: Số xe, NCC, Nhập BB (KG) và phân bổ BBA cho từng xe.
                                • Thực nhận BBA = tổng phân bổ BBA của xe • Tồn BBE = Thực nhận BB - Thực nhận BBA.
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={handleClose}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                            >
                                Đóng
                            </button>
                            <button
                                onClick={handleSaveMulti}
                                disabled={isSubmitting || (() => {
                                    // Check xem có lỗi validation không
                                    return vehicleRows.some(row => {
                                        if (row.allocations.length === 0) return false;
                                        const totalSoDay = row.allocations.reduce((s, a) => s + parseNumber(a.soDay), 0);
                                        const totalKL = row.allocations.reduce((s, a) => s + parseNumber(a.khoiLuong), 0);
                                        const soDayInput = parseNumber(row.soDay);
                                        const thucNhanBBA = parseNumber(row.thucNhanBBA);

                                        // Kiểm tra nếu tổng không khớp
                                        const soDayInvalid = soDayInput > 0 && Math.abs(totalSoDay - soDayInput) > 0.01;
                                        const klInvalid = thucNhanBBA > 0 && Math.abs(totalKL - thucNhanBBA) > 0.01;

                                        return soDayInvalid || klInvalid;
                                    });
                                })()}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
                                title={vehicleRows.some(row => {
                                    if (row.allocations.length === 0) return false;
                                    const totalSoDay = row.allocations.reduce((s, a) => s + parseNumber(a.soDay), 0);
                                    const totalKL = row.allocations.reduce((s, a) => s + parseNumber(a.khoiLuong), 0);
                                    const soDayInput = parseNumber(row.soDay);
                                    const thucNhanBBA = parseNumber(row.thucNhanBBA);
                                    const soDayInvalid = soDayInput > 0 && Math.abs(totalSoDay - soDayInput) > 0.01;
                                    const klInvalid = thucNhanBBA > 0 && Math.abs(totalKL - thucNhanBBA) > 0.01;
                                    return soDayInvalid || klInvalid;
                                }) ? "Vui lòng kiểm tra lại tổng số dây và khối lượng phân bổ" : ""}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                        Đang lưu...
                                    </>
                                ) : (
                                    <>
                                        <Check className="w-4 h-4" />
                                        Lưu tất cả xe
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
                        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                            <AlertCircle className="h-6 w-6 text-red-600" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                            {confirmTitle}
                        </h3>
                        <p className="text-sm text-gray-500 text-center mb-6">
                            {confirmMessage}
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={handleCloseConfirmModal}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                disabled={isConfirmLoading}
                            >
                                Hủy
                            </button>
                            <button
                                onClick={async () => {
                                    setIsConfirmLoading(true);
                                    await confirmAction();
                                    setIsConfirmLoading(false);
                                    handleCloseConfirmModal();
                                }}
                                disabled={isConfirmLoading}
                                className={`flex-1 px-4 py-2 bg-red-600 text-white rounded-lg ${isConfirmLoading
                                    ? 'opacity-50 cursor-not-allowed'
                                    : 'hover:bg-red-700'
                                    } transition-colors`}
                            >
                                {isConfirmLoading ? 'Đang xử lý...' : 'Xác nhận'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Các modal khác giữ nguyên */}
            {showPhanBoEmTongTheModal && (
                <PhanBoBaoBiEmTongThe
                    packagingData={packagingData}
                    phanBoData={detailReports}
                    onClose={() => setShowPhanBoEmTongTheModal(false)}
                    onSuccess={async () => {
                        await fetchPackagingData();
                        await fetchPhanBoData();
                        toast.success('Phân bổ bao bì em thành công!');
                    }}
                />
            )}

            {showChuyenBaoBiAnhSangEmModal && (
                <ChuyenBaoBiAnhSangEm
                    phanBoData={detailReports}
                    chuyenBaoBiData={chuyenBaoBiData}
                    onClose={() => setShowChuyenBaoBiAnhSangEmModal(false)}
                    onSuccess={async () => {
                        await fetchPackagingData();
                        await fetchPhanBoData();
                        await fetchChuyenBaoBiData();
                        toast.success('Chuyển bao bì thành công!');
                    }}
                />
            )}

            {showHieuChinhTonBBEModal && (
                <HieuChinhTonBBE
                    tonBBEHienTai={(() => {
                        // Sử dụng packagingData (toàn bộ) thay vì filteredData để tính tồn tổng
                        const totalThucNhanBBE = packagingData.reduce((sum, record) => sum + parseNumber(record['THỰC NHẬN BBE (KG)']), 0);
                        const totalAllocatedBBE = detailReports
                            .filter(pb => pb['TÊN HÀNG'] === 'Bao bì em' && !pb.ID_NHAPBAOBI)
                            .reduce((sum, pb) => sum + parseNumber(pb['KHỐI LƯỢNG']), 0);
                        return totalThucNhanBBE - totalAllocatedBBE;
                    })()}
                    hieuChinhData={hieuChinhTonBBEData}
                    onClose={() => setShowHieuChinhTonBBEModal(false)}
                    onSuccess={async () => {
                        await fetchHieuChinhTonBBEData();
                    }}
                />
            )}

            {showImportFullModal && (
                <ImportExcelNhapBaoBiVaPhanBo
                    onClose={() => setShowImportFullModal(false)}
                    onSuccess={async () => {
                        await fetchPackagingData();
                        await fetchPhanBoData();
                        toast.success('Nhập Excel thành công!');
                    }}
                />
            )}

            {showExportExcelModal && (
                <ExportExcelNhapBaoBi
                    packagingData={packagingData}
                    phanBoData={detailReports}
                    onClose={() => setShowExportExcelModal(false)}
                />
            )}

            {/* Modal hiển thị thông tin Thực nhận BBA/BBE/Trả BB */}
            {showThucNhanModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
                        {/* Config cho modal */}
                        {(() => {
                            let modalConfig = {
                                title: '',
                                fieldName: '',
                                headerColor: '',
                                textColor: '',
                                fileName: ''
                            };

                            switch (thucNhanType) {
                                case 'BBA':
                                    modalConfig = {
                                        title: 'Chi tiết Thực nhận BBA',
                                        fieldName: 'THỰC NHẬN BBA (KG)',
                                        headerColor: 'bg-gradient-to-r from-teal-500 to-teal-600',
                                        textColor: 'text-teal-600',
                                        fileName: 'thuc-nhan-bba'
                                    };
                                    break;
                                case 'BBE':
                                    modalConfig = {
                                        title: 'Chi tiết Thực nhận BBE',
                                        fieldName: 'THỰC NHẬN BBE (KG)',
                                        headerColor: 'bg-gradient-to-r from-pink-500 to-pink-600',
                                        textColor: 'text-pink-600',
                                        fileName: 'thuc-nhan-bbe'
                                    };
                                    break;
                                case 'TRABB':
                                    modalConfig = {
                                        title: 'Chi tiết Trả Bao Bì',
                                        fieldName: 'TRẢ BB (KG)',
                                        headerColor: 'bg-gradient-to-r from-yellow-500 to-yellow-600',
                                        textColor: 'text-yellow-600',
                                        fileName: 'tra-bao-bi'
                                    };
                                    break;
                                default:
                                    break;
                            }

                            return (
                                <>
                                    {/* Header */}
                                    <div className={`${modalConfig.headerColor} px-6 py-4 flex justify-between items-center`}>
                                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                            <Package className="w-6 h-6" />
                                            {modalConfig.title}
                                        </h2>
                                        <button
                                            onClick={() => setShowThucNhanModal(false)}
                                            className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Content */}
                                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50 sticky top-0">
                                                    <tr>
                                                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">STT</th>
                                                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ngày</th>
                                                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Số xe</th>
                                                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NCC</th>
                                                        <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Số lô</th>
                                                        <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            Khối lượng (KG)
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {packagingData
                                                        .filter(record => parseNumber(record[modalConfig.fieldName]) > 0)
                                                        .map((record, index) => {
                                                            const value = parseNumber(record[modalConfig.fieldName]);
                                                            return (
                                                                <tr key={record.ID} className="hover:bg-gray-50 transition-colors">
                                                                    <td className="px-3 py-2.5 text-sm text-gray-700">{index + 1}</td>
                                                                    <td className="px-3 py-2.5 text-sm text-gray-700">
                                                                        {new Date(record['NGÀY THÁNG']).toLocaleDateString('vi-VN')}
                                                                    </td>
                                                                    <td className="px-3 py-2.5 text-sm text-gray-900 font-medium">{record['SỐ XE']}</td>
                                                                    <td className="px-3 py-2.5 text-sm text-gray-700">{record['NCC']}</td>
                                                                    <td className="px-3 py-2.5 text-sm text-gray-700">{record['SỐ LÔ']}</td>
                                                                    <td className={`px-3 py-2.5 text-sm text-right font-semibold ${modalConfig.textColor}`}>
                                                                        {formatNumber(value)}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                </tbody>
                                                <tfoot className="bg-gray-50 font-bold">
                                                    <tr>
                                                        <td colSpan="5" className="px-3 py-2.5 text-sm text-gray-900 text-right">Tổng cộng:</td>
                                                        <td className={`px-3 py-2.5 text-sm text-right ${modalConfig.textColor}`}>
                                                            {formatNumber(
                                                                packagingData
                                                                    .filter(record => parseNumber(record[modalConfig.fieldName]) > 0)
                                                                    .reduce((sum, record) => sum + parseNumber(record[modalConfig.fieldName]), 0)
                                                            )} KG
                                                        </td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 bg-gray-50">
                                        <button
                                            onClick={() => {
                                                // Export to Excel
                                                const dataToExport = packagingData
                                                    .filter(record => parseNumber(record[modalConfig.fieldName]) > 0)
                                                    .map((record, index) => ({
                                                        'STT': index + 1,
                                                        'Ngày': new Date(record['NGÀY THÁNG']).toLocaleDateString('vi-VN'),
                                                        'Số xe': cleanTextValue(record['SỐ XE']),
                                                        'NCC': record['NCC'],
                                                        'Số lô': cleanTextValue(record['SỐ LÔ']),
                                                        [`${modalConfig.fieldName}`]: parseNumber(record[modalConfig.fieldName])
                                                    }));

                                                const ws = XLSX.utils.json_to_sheet(dataToExport);
                                                formatWorksheetTextColumns(ws); // Format cột Số xe và Số lô thành text
                                                const wb = XLSX.utils.book_new();
                                                XLSX.utils.book_append_sheet(wb, ws, modalConfig.title);
                                                XLSX.writeFile(wb, `${modalConfig.fileName}-${new Date().toISOString().slice(0, 10)}.xlsx`);
                                            }}
                                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                                        >
                                            <Download className="w-4 h-4" />
                                            Xuất Excel
                                        </button>
                                        <button
                                            onClick={() => setShowThucNhanModal(false)}
                                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            Đóng
                                        </button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </div>
    );
};

export default NhapBaoBiManagement;
